import React, {
  useCallback, useEffect, useState, useRef, useMemo, Fragment, RefObject, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Card from '../../components/card/card.component';
import Table from '../../components/table/table.component';
import TableHead from '../../components/table-head/table-head.component';
import TableBody from '../../components/table-body/table-body.component';
import TableRow from '../../components/table-row/table-row.component';
import TableCell from '../../components/table-cell/table-cell.component';
import HiddenBlock from '../../components/hidden-block/hidden-block.component';
import DraggableItem from '../../components/draggable-item/draggable-item.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import List from '../../components/list/list.component';
import ListItemImage from '../../components/list-item-image/list-item-image.component';
import ListItemText from '../../components/list-item-text/list-item-text.component';
import ListItemSummary from '../../components/list-item-summary/list-item-summary.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import ExpansionGroup from '../../components/expansion-group/expansion-group.component';
import TableResize from '../../components/table-resize/table-resize.component';
import LongClickListItem from '../../components/long-click-list-item/long-click-list-item.component';
import CheckAccess from '../../components/check-access/check-access.component';
import RequiredMark from '../../components/required-mark/required-mark.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import DragHandle from '../../components/drag-handle/drag-handle.component';
import LocationMap from '../../components/location-map/location-map.component';
import FilteredCell from '../../components/filtered-cell/filtered-cell.component';
import ContextMenu from '../../components/context-menu/context-menu.component';
import FilteredLink from '../../components/filtered-link/filtered-link.component';
import PaddedFlexContainer from '../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../components/flex-container/flex-container.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import Image from '../../components/image/image.component';
import Chip from '../../components/chip/chip.component';
import ChipLink from '../../components/chip-link/chip-link.component';
import TableSortLabel from '../../components/table-sort-label/table-sort-label.component';
import NoContentBlock from '../../components/no-content-block/no-content-block.component';
import ActionButtonContainer from '../../components/action-button-container/action-button-container.component';
import ActionButton from '../../components/action-button/action-button.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import Checkbox from '../../components/checkbox/checkbox.component';
import ListItemCheckbox from '../../components/list-item-checkbox/list-item-checkbox.component';
import TextBlock from '../../components/text-block/text-block.component';
import TextBold from '../../components/text-bold/text-bold.component';
import VisibleCell from '../../components/visible-cell/visible-cell.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import FormError from '../../components/form-error/form-error.component';
import FormFieldText from '../../components/form-field-text/form-field-text.component';
import ExpansionGroupWidget from '../../components/expansion-group-widget/expansion-group-widget.component';
import TableCellShort from '../../components/table-cell-short/table-cell-short.component';
import TableCellExtended from '../../components/table-cell-extended/table-cell-extended.component';
import TableRowSummary from '../../components/table-row-summary/table-row-summary.component';
import TableRowNoContent from '../../components/table-row-no-content/table-row-no-content.component';
import TableRowGroupHeader from '../../components/table-row-group-header/table-row-group-header.component';
import TableRowGroupName from '../../components/table-row-group-name/table-row-group-name.component';
import ContentListContainerOffset
  from '../app/common/content-list-container-offset/content-list-container-offset.component';
import StatisticsBlock from '../../components/statistics-block/statistics-block.component';
import ControlButtonBlock from '../app/common/control-button-block/control-button-block.component';
import Controls from '../app/controls/controls.component';
import ExportModal from '../app/controls/export-modal/export-modal.component';
import PortfolioFilter from '../app/controls/portfolio-filter/portfolio-filter.component';
import PortfolioGroupModal from './portfolio-group-modal/portfolio-group-modal.component';
import PortfolioModal from './portfolio-modal/portfolio-modal.component';
import AssetModal from './asset-modal/asset-modal.component';
import AssetRow from './asset-row/asset-row.component';
import EditIcon from '../../icons/edit-icon';
import ExpandIcon from '../../icons/expand-icon';
import CollapseIcon from '../../icons/collapse-icon';
import DeleteItemIcon from '../../icons/delete-item-icon';
import MoreIcon from '../../icons/more-icon';
import ArrowLinkIcon from '../../icons/arrow-link-icon';
import AssetListValidator from './asset-list.validator';
import SortService from '../../services/sort-service';
import GoogleMapService from '../../services/google-map-service';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import ScreenService from '../../services/screen-service';
import DateService from '../../services/date-service';
import FormService from '../../services/form-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useArrowNavigation from '../../hooks/use-arrow-navigation';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useScreenMobile from '../../hooks/use-screen-mobile';
import useScreenSmall from '../../hooks/use-screen-small';
import useMobileDevice from '../../hooks/use-mobile-device';
import useScroll from '../../hooks/use-scroll';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import {
  getAssetListData, deleteAssetList, deletePortfolio, saveAssetListData, savePortfolioOrderSettings,
} from './asset-list.action';
import {
  toggleEditMode, toggleExportMode, toggleCustomizeMode, toggleSelectMode, saveTableColumnSettings,
} from '../app/controls/controls.action';
import {
  setPortfolioIdsFilter, setPortfolioGroupIdsFilter,
} from '../app/controls/portfolio-filter/portfolio-filter.action';
import { getListContactCompanyData, getListTagData } from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../constants/system/sort-types';
import { FORM_ID_ASSET_LIST } from '../../constants/system/form-ids';
import { FORMAT_LIST_IMPORT_ASSET, FORMAT_LIST_EXPORT_ASSET } from '../../constants/system/format-lists';
import { PAGE_LIST_DEFAULT } from '../../constants/system/pages';
import {
  ASSET_CREATE, ASSET_EDIT, ASSET_LIST_IMPORT, ASSET_LIST_EXPORT, ASSET_PORTFOLIO_FILTER_VIEW,
  ASSET_STATISTICS_VIEW, ASSET_LIST_VIEW, ASSET_DELETE, PORTFOLIO_DELETE, PORTFOLIO_EDIT, PORTFOLIO_CREATE,
  ASSET_LIST_ASSET_LOCATIONS_VIEW, DEAL_VIEW, ASSET_VIEW, SEARCH_RESULT_VIEW, CONTACT_VIEW, PORTFOLIO_GROUP_CREATE,
} from '../../constants/entity/permissions';
import {
  ROUTE_ASSET, ROUTE_CONTACT, ROUTE_SEARCH, ROUTE_DEAL, ROUTE_ASSET_LIST,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_EDIT_MODE, QUERY_PARAM_PORTFOLIO_IDS, QUERY_PARAM_PORTFOLIO_GROUP_IDS,
} from '../../constants/system/query-params';
import { DATA_ASSET_ID, DATA_PORTFOLIO_ID } from '../../constants/system/data-attributes';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../constants/entity/setting-names';
import { IMPORT_EXPORT_ENTITY_TYPE_ASSET_LIST } from '../../constants/system/import-export-entity-types';
import { TABLE_NAME_ASSET_LIST } from '../../constants/system/table-names';
import { MARKER_COLOR_RED } from '../../constants/entity/marker-colors';
import { SIGN_DOT } from '../../constants/system/signs';
import ASSET_STATUS_UNREALIZED from '../../constants/entity/asset-statuses';
import AssetDefaultImage from '../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../static/images/company-default-image.png';

const AssetList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HEADER_NAME_ACTIONS: string = 'actions';
  const ID_PREFIX_CHECKBOX_EXPORT_GROUP: string = 'checkbox-export-group';
  const GROUP_ID_SUMMARY: number = 0;

  const {
    isDataLoaded,
    isLoading,
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isRightDrawerOpen,
    assetListData,
    assetListSummaryData,
    assetsTotal,
    currentUser,
    statisticsData,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    assetStatusList,
    assetTypeList,
    measureUnitList,
    pricingTypeList,
    tagList,
    assetOwnershipStructureTypeList,
    contactCompanyList,
    portfolioMarkerColorList,
    portfolioGroupList,
    portfolioList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const isMobileScreen: boolean = useScreenMobile();
  const isSmallScreen: boolean = useScreenSmall();
  const isMobileDevice: boolean = useMobileDevice();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const permissionList: string[] = useCurrentUserPermissions();
  const tableRefs: RefObject<Record<string, HTMLTableElement>> = useRef({});
  const headerCellRefs: RefObject<Record<string, Record<string, HTMLElement>>> = useRef({});
  const portfolioFieldRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const dispatch: ReduxDispatch = useDispatch();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryFilterPortfolioIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS].toString().split(SIGN_DOT)
        .map((portfolioId: string): number => FormatService.parseInt(portfolioId)) : null
  );
  const queryFilterPortfolioGroupIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS].toString().split(SIGN_DOT)
        .map((portfolioGroupId: string): number => FormatService.parseInt(portfolioGroupId)) : null
  );

  const assetEditPermissions: string[] = useMemo((): string[] => [ASSET_EDIT], []);
  const assetListViewPermissions: string[] = useMemo((): string[] => [ASSET_LIST_VIEW], []);
  const assetStatisticsViewPermissions: string[] = useMemo((): string[] => [ASSET_STATISTICS_VIEW], []);
  const assetPortfolioFilterPermissions: string[] = useMemo((): string[] => [ASSET_PORTFOLIO_FILTER_VIEW], []);
  const portfolioEditPermissions: string[] = useMemo((): string[] => [PORTFOLIO_EDIT], []);
  const portfolioDeletePermissions: string[] = useMemo((): string[] => [PORTFOLIO_DELETE], []);

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const GROUP_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'order',
    type: SORT_TYPE_ASC,
    isNumber: true,
  }), []);

  const [menuAnchor, setMenuAnchor]: [MaterialUIMenuAnchorElement | null, Function] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor]: [MaterialUIMenuAnchorElement | null, Function] = useState(null);
  const [page, setPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [sortParams, setSortParams]: [Record<string, SortParams> | null, Function] = useState<StateValueDefault>(null);
  const [isAssetModalOpen, setIsAssetModalOpen]: [boolean, Function] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen]: [boolean, Function] = useState(false);
  const [isPortfolioGroupModalOpen, setIsPortfolioGroupModalOpen]: [boolean, Function] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen]: [boolean, Function] = useState(false);
  const [isAssetDeleteConfirmModalOpen, setIsAssetDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isPortfolioDeleteConfirmModalOpen, setIsPortfolioDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [selectedRows, setSelectedRows]: [Record<string, number[]>, Function] = useState({});
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [assetActiveIds, setAssetActiveIds]: [number[], Function] = useState([]);
  const [portfolioActiveIds, setPortfolioActiveIds]: [number[], Function] = useState([]);
  const [assetPhotoFiles, setAssetPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [portfolioGroupPhotoFiles, setPortfolioGroupPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [isNewAsset, setIsNewAsset]: [boolean, Function] = useState(false);
  const [isNewPortfolio, setIsNewPortfolio]: [boolean, Function] = useState(false);
  const [isNewPortfolioGroup, setIsNewPortfolioGroup]: [boolean, Function] = useState(false);
  const [selectedRowsDefault, setSelectedRowsDefault]: [Record<string, number[]>, Function] = useState({});
  const [assetAddressList, setAssetAddressList]: [Record<string, JsonObject> | null, Function]
    = useState<StateValueDefault>(null);
  const [isNewSellerVisible, setIsNewSellerVisible]: [boolean, Function] = useState(false);
  const [expandedGroups, setExpandedGroups]: [number[], Function] = useState([]);
  const [portfolioActiveId, setPortfolioActiveId]: [number | null, Function] = useState(null);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [tableColumnSettings, setTableColumnSettings]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastPortfolioIdsValue, setLastPortfolioIdsValue]: [number[] | null, Function] = useState(null);
  const [lastPortfolioGroupIdsValue, setLastPortfolioGroupIdsValue]: [number[] | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [assetFieldNewItems, setAssetFieldNewItems]: [Record<string, AssetFieldInput[]>, Function]
    = useState<StateValueDefault>({});
  const [withAllRowsSelected, setWithAllRowsSelected]: [boolean, Function] = useState(false);
  const [inlineSaveAssetId, setInlineSaveAssetId]: [number | null, Function] = useState(null);
  const [inlineSavePortfolioId, setInlineSavePortfolioId]: [number | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);
  const [activePortfolioIndex, setActivePortfolioIndex]: [number | null, Function] = useState(null);
  const [hoverPortfolioIndex, setHoverPortfolioIndex]: [number | null, Function] = useState(null);

  const portfolioAssetList: Asset[] = useMemo((): Asset[] => (
    assetListData && assetListData.length
      ? assetListData.flatMap((portfolioItem: Portfolio): Asset[] | undefined => portfolioItem.assets)
        .filter((assetItem: Asset[] | undefined): boolean => !!assetItem) as Asset[] : []
  ), [assetListData]);
  const portfolioGroupListFiltered: PortfolioGroup[] = useMemo((): PortfolioGroup[] => (
    portfolioGroupList && portfolioGroupList.filter((portfolioGroupItem: PortfolioGroup): boolean => (
      !!portfolioGroupItem.portfolios.length
    ))
  ), [portfolioGroupList]);
  const commonAssetsTotal: number = useMemo((): number => (
    assetListData && assetListData.length ? assetListData.reduce((
      assetItemsTotal: number, portfolioItem: Portfolio,
    ): number => (
      assetItemsTotal + portfolioItem.assets.length
    ), 0) : 0
  ), [assetListData]);
  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioGroupListFiltered && portfolioGroupListFiltered.length > 1
    && !portfolioGroupIdsFilter && !portfolioIdsFilter
  ), [portfolioGroupListFiltered, portfolioGroupIdsFilter, portfolioIdsFilter]);
  const assetsSelectedTotal: number = useMemo((): number => (
    selectedRows
      ? Object.values(selectedRows).reduce((resultTotal: number, selectedRowIds: number[]): number => (
        resultTotal + selectedRowIds.length
      ), 0) : 0
  ), [selectedRows]);
  const fileDropDeps: DepsItem[] = useMemo((): DepsItem[] => [assetPhotoFiles], [assetPhotoFiles]);
  const tableColumnDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isCustomizeMode, tableColumnSettings,
  ], [isCustomizeMode, tableColumnSettings]);
  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    assetsTotal, commonAssetsTotal, isLoading, isEditMode, isCustomizeMode,
  ], [assetsTotal, commonAssetsTotal, isLoading, isEditMode, isCustomizeMode]);
  const portfolioMarkerColorIdList: number[] = useMemo((): number[] => (
    portfolioList && portfolioList.length ? portfolioList.map((portfolioItem: Portfolio): number | null => (
      portfolioItem.markerColor ? portfolioItem.markerColor.id : null
    )).filter((portfolioMarkerColorId: number | null): boolean => !!portfolioMarkerColorId) as number[] : []
  ), [portfolioList]);
  const portfolioAssetLocationItemList: LocationMapItem[] = useMemo((): LocationMapItem[] => (
    portfolioAssetList && portfolioAssetList.length
      ? portfolioAssetList.map((assetItem: Asset): LocationMapItem => ({
        id: assetItem.id,
        title: assetItem.name || null,
        color: (assetItem.portfolio && assetItem.portfolio.markerColor && assetItem.portfolio.markerColor
          && assetItem.portfolio.markerColor.name) || null,
        link: `${ROUTE_ASSET}/${assetItem.id}`,
        amount: assetItem.amount || null,
        imageLink: assetItem.mainPhotoThumbnail || assetItem.mainPhoto,
        latitude: assetItem.latitude || null,
        longitude: assetItem.longitude || null,
      })) : []
  ), [portfolioAssetList]);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const isFormDataPristine: boolean = isFormPristine && !Object.keys(assetPhotoFiles).length
    && !Object.keys(assetFieldNewItems).length && !Object.keys(portfolioGroupPhotoFiles).length;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isExportMode || isSelectMode || isCustomizeMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const formPrefix: string = 'assetListData';
  const withMap: boolean = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode;
  const withButtons: boolean = isEditMode || isExportMode || isSelectMode;
  const isPortfolioEditAllowed: boolean = StructureService.checkPermission(PORTFOLIO_EDIT, permissionList);
  const isPortfolioFilterViewAllowed: boolean = StructureService.checkPermission(
    ASSET_PORTFOLIO_FILTER_VIEW, permissionList,
  );
  const isGroupOrderAllowed: boolean = isCustomizeMode && expandedGroups && !expandedGroups.length
    && !isMobileDevice && !portfolioIdsFilter && !portfolioGroupIdsFilter && isPortfolioEditAllowed;
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && !!lastLocation;
  const portfolioIds: number[] = assetListData && assetListData.length
    ? assetListData.map((portfolioItem: Portfolio): number => portfolioItem.id) : [];
  const pageTitle: string = 'Asset Portfolio';
  const createButtonTitle: string | null = !isExportMode && !isSelectMode && !isCustomizeMode ? 'New Asset' : null;
  const createGroupButtonTitle: string | null = !isExportMode && !isSelectMode && !isEditMode ? 'New Portfolio' : null;
  const createAdditionalItemButtonTitle: string | null = !isExportMode && !isSelectMode && !isEditMode
    ? 'New Group' : null;
  const isGroupExpanded: boolean = expandedGroups && !!expandedGroups.length;
  const isCustomizable: boolean = !isSmallScreen;
  const assetDeleteConfirmModalTitle: string = assetActiveIds && assetActiveIds.length > 1
    ? 'Delete Assets' : 'Delete Asset';
  const assetDeleteConfirmModalText: string = assetActiveIds && assetActiveIds.length > 1
    ? 'Are you sure you want to delete these assets?' : 'Are you sure you want to delete this asset?';
  const portfolioDeleteConfirmModalTitle: string = 'Delete Portfolio';
  const portfolioDeleteConfirmModalText: string = 'Are you sure you want to delete this portfolio?';
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  let commonRowIndex: number = 0;
  let isTableLoading: boolean = false;

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const updateQueryParams: Function = useCallback((): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioGroupIdsFilter && portfolioGroupIdsFilter.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioGroupIdsFilter.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ASSET_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const closeAssetModal: Function = useCallback((): void => {
    setIsAssetModalOpen(false);
    setIsNewAsset(false);

    if (isEditMode) {
      setInlineSaveAssetId(FormService.FORM_KEY_NEW);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, assetActiveIds, portfolioActiveIds]);

  const onSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const closePortfolioModal: Function = useCallback((): void => {
    setIsPortfolioModalOpen(false);
    setIsNewPortfolio(false);
  }, []);

  const closePortfolioGroupModal: Function = useCallback((): void => {
    setIsPortfolioGroupModalOpen(false);
    setIsNewPortfolioGroup(false);
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const onGroupFieldClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
      setLastPortfolioIdsValue(undefined);
      setLastPortfolioGroupIdsValue(undefined);

      return false;
    }

    if (!isNewAsset) {
      if (isEditMode) {
        dispatch(toggleEditMode(false));
      }
      if (isExportMode) {
        dispatch(toggleExportMode(false));
      }
      if (isSelectMode) {
        dispatch(toggleSelectMode(false));
      }
      if (isCustomizeMode) {
        dispatch(toggleCustomizeMode(false));
      }
    }

    return true;
  }, [isFormChanged, isNewAsset, isEditMode, isExportMode, isSelectMode, isCustomizeMode]);

  const openExpansionGroups: Function = useCallback((): void => {
    if (assetListData && assetListData.length) {
      setExpandedGroups(
        assetListData.map((portfolioItem: Portfolio, portfolioIndex: number): number => portfolioIndex + 1),
      );
    }
  }, [assetListData]);

  const closeExpansionGroups: Function = useCallback((): void => {
    setExpandedGroups([]);
  }, []);

  const getOptions: Function = useCallback((
    portfolioIdsOption?: number[], portfolioGroupIdsOption?: number[], pageOption?: number, allRowsOption?: boolean,
  ): RequestOptions => {
    let options: RequestOptions = {};

    options = portfolioIdsOption && portfolioIdsOption.length
      ? { ...options, portfolioIds: portfolioIdsOption } : options;
    options = portfolioGroupIdsOption && portfolioGroupIdsOption.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsOption } : options;
    options = pageOption ? { ...options, page: pageOption } : options;
    options = allRowsOption ? { ...options, allRows: allRowsOption } : options;

    return options;
  }, []);

  const checkIsAssetActive: Function = useCallback((assetId: number): boolean => (
    assetActiveIds ? assetActiveIds.includes(assetId) : false
  ), [assetActiveIds]);

  const checkIsPortfolioActive: Function = useCallback((portfolioId: number): boolean => (
    portfolioActiveIds ? portfolioActiveIds.includes(portfolioId) : false
  ), [portfolioActiveIds]);

  const inlineCancel: Function = useCallback((): void => {
    const portfolioId: number | null = inlineSavePortfolioId;
    const assetId: number | null = inlineSaveAssetId;

    if (assetId && portfolioId) {
      const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
      const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

      if (initialFormValues && initialFormValues.assetListData && initialFormValues.assetListData[formKeyPortfolio]
        && initialFormValues.assetListData[formKeyPortfolio].assets) {
        changeForm(
          `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}]`,
          initialFormValues.assetListData[formKeyPortfolio].assets[formKeyAsset],
        );
      }

      if (assetId !== FormService.FORM_KEY_NEW) {
        const assetActiveIdIndex: number = assetActiveIds.findIndex((assetItemActiveId: number): boolean => (
          assetItemActiveId === assetId
        ));

        if (assetActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newAssetActiveIds: number[] = [...assetActiveIds];
          StructureService.deleteListItemByIndex(newAssetActiveIds, assetActiveIdIndex);
          setAssetActiveIds(newAssetActiveIds);
        }
      }

      if (assetPhotoFiles[assetId]) {
        const newAssetPhotoFiles: Record<string, File> = { ...assetPhotoFiles };
        delete newAssetPhotoFiles[assetId];
        setAssetPhotoFiles(newAssetPhotoFiles);
      }

      if (assetFieldNewItems[assetId]) {
        const newAssetFieldNewItems: Record<string, AssetFieldInput[]> = { ...assetFieldNewItems };
        delete newAssetFieldNewItems[assetId];
        setAssetFieldNewItems(newAssetFieldNewItems);
      }
    }

    setInlineSaveIsConfirm(null);
    setInlineSaveAssetId(null);
    setInlineSavePortfolioId(null);
  }, [
    initialFormValues, assetActiveIds, assetPhotoFiles, assetFieldNewItems, inlineSavePortfolioId, inlineSaveAssetId,
  ]);

  const onCancelForm: Function = useCallback((): void => {
    if (inlineSaveAssetId !== null && inlineSavePortfolioId !== null) {
      if (isAssetModalOpen) {
        closeAssetModal();
      }

      inlineCancel();
    } else {
      if (isAssetModalOpen) {
        closeAssetModal();
      }

      if (isPortfolioModalOpen) {
        closePortfolioModal();
      }

      if (isPortfolioGroupModalOpen) {
        closePortfolioGroupModal();
      }

      FormService.forceResetForm(resetForm, formProps);

      setAssetPhotoFiles({});
      setAssetFieldNewItems({});
      setAssetActiveIds([]);
      setPortfolioActiveIds([]);
      setPortfolioGroupPhotoFiles({});
      setSelectedRows(selectedRowsDefault);
      setWithAllRowsSelected(false);
      setIsNewSellerVisible(false);

      assetListData.forEach((portfolioItem: Portfolio): void => {
        selectedRowsDefault[portfolioItem.id] = [];
      });

      if (!isNewAsset) {
        dispatch(toggleEditMode(false));
        dispatch(toggleExportMode(false));
        dispatch(toggleSelectMode(false));
        dispatch(toggleCustomizeMode(false));
      }
    }
  }, [
    assetListData, selectedRowsDefault, isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles,
    assetFieldNewItems,
  ]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    let assetListFormData: FormValues = {};
    let portfolioGroupListFormData: FormValues = {};

    if (isNewAsset) {
      if (values.assetListData[formKeyNew].assets[formKeyNew]) {
        assetListFormData[formKeyNew] = {
          id: FormService.FORM_KEY_NEW,
          name: '',
          assets: [
            {
              ...values.assetListData[formKeyNew].assets[formKeyNew],
            },
          ],
        };
      }
    } else if (isNewPortfolio) {
      if (values.assetListData[formKeyNew].assets[formKeyNew]) {
        assetListFormData[formKeyNew] = {
          id: FormService.FORM_KEY_NEW,
          name: values.assetListData[formKeyNew].name,
          capitalAllocated: values.assetListData[formKeyNew].capitalAllocated
            ? FormatService.formatCleanNumber(values.assetListData[formKeyNew].capitalAllocated) : 0,
          groupId: values.assetListData[formKeyNew].groupId,
          markerColor: values.assetListData[formKeyNew].markerColor,
          assets: [],
        };
      }
    } else if (isNewPortfolioGroup) {
      portfolioGroupListFormData[formKeyNew] = {
        ...values.portfolioGroupListData[formKeyNew],
      };
    } else {
      assetListFormData = {
        ...values.assetListData,
      };
    }

    if (!Object.keys(assetListFormData).length && !Object.keys(portfolioGroupListFormData)) {
      return null;
    }

    const assetList: PortfolioInput[] = [];
    if (assetListFormData && Object.keys(assetListFormData).length) {
      Object.keys(assetListFormData).forEach((portfolioIndex: string): void => {
        const portfolioFormItem: FormValueItem = {
          ...assetListFormData[portfolioIndex],
        };

        const portfolioAssetActiveIds: number[] = portfolioFormItem && portfolioFormItem.assets
        && Object.keys(portfolioFormItem.assets).length
          ? Object.values(portfolioFormItem.assets).filter((assetItem: FormValueItem): boolean => !!assetItem)
            .map((assetItem: FormValueItem): number => assetItem.id)
            .filter((assetId: number): boolean => checkIsAssetActive(assetId)) : [];

        if (portfolioFormItem && (isNewAsset || isNewPortfolio || portfolioAssetActiveIds.length
          || (inlineSavePortfolioId && inlineSavePortfolioId === portfolioFormItem.id)
          || (!inlineSavePortfolioId && checkIsPortfolioActive(portfolioFormItem.id)))) {
          const portfolioItem: PortfolioInput = {
            name: portfolioFormItem.name,
            capitalAllocated: portfolioFormItem.capitalAllocated
              ? FormatService.formatCleanNumber(portfolioFormItem.capitalAllocated, true) : 0,
            groupId: portfolioFormItem.groupId ? FormatService.formatCleanNumber(portfolioFormItem.groupId) : 0,
            markerColor: portfolioFormItem.markerColor,
            assets: [],
          };

          if (portfolioFormItem.id || portfolioFormItem.id === FormService.FORM_KEY_NEW) {
            portfolioItem.id = portfolioFormItem.id;
          }

          Object.keys(portfolioFormItem.assets).forEach((assetIndex: string): void => {
            const assetFormItem: FormValueItem = {
              ...portfolioFormItem.assets[assetIndex],
            };

            if (assetFormItem && (isNewAsset || (inlineSaveAssetId && inlineSaveAssetId === assetFormItem.id)
              || (!inlineSaveAssetId && checkIsAssetActive(assetFormItem.id)))) {
              const assetId: number = isNewAsset ? FormService.FORM_KEY_NEW : assetFormItem.id;

              delete assetFormItem.location;
              delete assetFormItem.streetAddress;
              delete assetFormItem.postalCode;

              assetFormItem.types = assetFormItem.types && assetFormItem.types.length
                ? assetFormItem.types.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
              assetFormItem.size = assetFormItem.size ? FormatService.formatCleanNumber(assetFormItem.size) : 0;
              assetFormItem.loanAmount = assetFormItem.loanAmount
                ? FormatService.formatCleanNumber(assetFormItem.loanAmount, true) : 0;
              assetFormItem.amount = assetFormItem.amount ? FormatService.formatCleanNumber(assetFormItem.amount) : 0;
              assetFormItem.value = assetFormItem.value ? FormatService.formatCleanNumber(assetFormItem.value) : 0;
              assetFormItem.cost = assetFormItem.cost ? FormatService.formatCleanNumber(assetFormItem.cost) : 0;
              assetFormItem.currentNoi = assetFormItem.currentNoi
                ? FormatService.formatCleanNumber(assetFormItem.currentNoi) : 0;
              assetFormItem.ttmNoi = assetFormItem.ttmNoi
                ? FormatService.formatCleanNumber(assetFormItem.ttmNoi) : 0;
              assetFormItem.runRateNoi = assetFormItem.runRateNoi
                ? FormatService.formatCleanNumber(assetFormItem.runRateNoi) : 0;
              assetFormItem.forwardNoi = assetFormItem.forwardNoi
                ? FormatService.formatCleanNumber(assetFormItem.forwardNoi) : 0;
              assetFormItem.maturityDate = assetFormItem ? DateService.formatDate(assetFormItem.maturityDate) : null;
              assetFormItem.sellerIds = assetFormItem.sellerIds && assetFormItem.sellerIds.length
                ? assetFormItem.sellerIds.map((contactItem: Contact): number => contactItem.id) : [];
              assetFormItem.tags = assetFormItem.tags && assetFormItem.tags.length
                ? assetFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];
              assetFormItem.portfolioInvestedEquity = assetFormItem.portfolioInvestedEquity
                ? FormatService.formatCleanNumber(assetFormItem.portfolioInvestedEquity) : 0;
              assetFormItem.leveredIrr = assetFormItem.leveredIrr
                ? FormatService.formatCleanNumber(assetFormItem.leveredIrr, true) : 0;
              assetFormItem.equityMultiple = assetFormItem.equityMultiple
                ? FormatService.formatCleanNumber(assetFormItem.equityMultiple, true) : 0;
              assetFormItem.futureNoi = assetFormItem.futureNoi
                ? FormatService.formatCleanNumber(assetFormItem.futureNoi) : 0;
              assetFormItem.investmentAmountCommitted = assetFormItem.investmentAmountCommitted
                ? FormatService.formatCleanNumber(assetFormItem.investmentAmountCommitted) : 0;
              assetFormItem.investmentAmountUnfunded = assetFormItem.investmentAmountUnfunded
                ? FormatService.formatCleanNumber(assetFormItem.investmentAmountUnfunded) : 0;
              assetFormItem.investmentAmountFunded = assetFormItem.investmentAmountFunded
                ? FormatService.formatCleanNumber(assetFormItem.investmentAmountFunded) : 0;
              assetFormItem.currentMarketValue = assetFormItem.currentMarketValue
                ? FormatService.formatCleanNumber(assetFormItem.currentMarketValue) : 0;
              assetFormItem.closeDate = assetFormItem.closeDate
                ? DateService.formatDate(assetFormItem.closeDate) : null;
              assetFormItem.amountInvested = assetFormItem.amountInvested
                ? FormatService.formatCleanNumber(assetFormItem.amountInvested) : 0;
              assetFormItem.projectedStabilizedNoi = assetFormItem.projectedStabilizedNoi
                ? FormatService.formatCleanNumber(assetFormItem.projectedStabilizedNoi) : 0;
              assetFormItem.goingInCapRate = assetFormItem.goingInCapRate
                ? FormatService.formatCleanNumber(assetFormItem.goingInCapRate, true) : 0;
              assetFormItem.untrendedYieldToCost = assetFormItem.untrendedYieldToCost
                ? FormatService.formatCleanNumber(assetFormItem.untrendedYieldToCost) : 0;
              assetFormItem.projectedUnleveredIrr = assetFormItem.projectedUnleveredIrr
                ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredIrr, true) : 0;
              assetFormItem.projectedUnleveredEquityMultiple = assetFormItem.projectedUnleveredEquityMultiple
                ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredEquityMultiple) : 0;
              assetFormItem.projectedUnleveredNetProfit = assetFormItem.projectedUnleveredNetProfit
                ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredNetProfit) : 0;
              assetFormItem.projectedLeveredIrr = assetFormItem.projectedLeveredIrr
                ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredIrr, true) : 0;
              assetFormItem.projectedLeveredEquityMultiple = assetFormItem.projectedLeveredEquityMultiple
                ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredEquityMultiple) : 0;
              assetFormItem.projectedLeveredNetProfit = assetFormItem.projectedLeveredNetProfit
                ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredNetProfit) : 0;
              assetFormItem.seniorLoanAmount = assetFormItem.seniorLoanAmount
                ? FormatService.formatCleanNumber(assetFormItem.seniorLoanAmount) : 0;
              assetFormItem.seniorLoanClosingDate = assetFormItem.seniorLoanClosingDate
                ? DateService.formatDate(assetFormItem.seniorLoanClosingDate) : null;
              assetFormItem.seniorLoanMaturityDate = assetFormItem.seniorLoanMaturityDate
                ? DateService.formatDate(assetFormItem.seniorLoanMaturityDate) : null;
              assetFormItem.seniorLoanInterestSpread = assetFormItem.seniorLoanInterestSpread
                ? FormatService.formatCleanNumber(assetFormItem.seniorLoanInterestSpread, true) : 0;
              assetFormItem.seniorLoanLtv = assetFormItem.seniorLoanLtv
                ? FormatService.formatCleanNumber(assetFormItem.seniorLoanLtv, true) : 0;
              assetFormItem.seniorLoanLtc = assetFormItem.seniorLoanLtc
                ? FormatService.formatCleanNumber(assetFormItem.seniorLoanLtc, true) : 0;
              assetFormItem.mezzanineLoanAmount = assetFormItem.mezzanineLoanAmount
                ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanAmount) : 0;
              assetFormItem.mezzanineLoanClosingDate = assetFormItem.mezzanineLoanClosingDate
                ? DateService.formatDate(assetFormItem.mezzanineLoanClosingDate) : null;
              assetFormItem.mezzanineLoanMaturityDate = assetFormItem.mezzanineLoanMaturityDate
                ? DateService.formatDate(assetFormItem.mezzanineLoanMaturityDate) : null;
              assetFormItem.mezzanineLoanInterestSpread = assetFormItem.mezzanineLoanInterestSpread
                ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanInterestSpread, true) : 0;
              assetFormItem.mezzanineLoanLtv = assetFormItem.mezzanineLoanLtv
                ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanLtv, true) : 0;
              assetFormItem.mezzanineLoanLtc = assetFormItem.mezzanineLoanLtc
                ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanLtc, true) : 0;
              assetFormItem.blendedLoanInterestSpread = assetFormItem.blendedLoanInterestSpread
                ? FormatService.formatCleanNumber(assetFormItem.blendedLoanInterestSpread, true) : 0;
              assetFormItem.notePrincipalBalanceAtOrigination = assetFormItem.notePrincipalBalanceAtOrigination
                ? FormatService.formatCleanNumber(assetFormItem.notePrincipalBalanceAtOrigination) : 0;
              assetFormItem.accruedInterest = assetFormItem.accruedInterest
                ? FormatService.formatCleanNumber(assetFormItem.accruedInterest) : 0;
              assetFormItem.yearBuilt = assetFormItem.yearBuilt
                ? FormatService.formatCleanNumber(assetFormItem.yearBuilt, false, true) : 0;
              assetFormItem.yearRenovated = assetFormItem.yearRenovated
                ? FormatService.formatCleanNumber(assetFormItem.yearRenovated, false, true) : 0;
              assetFormItem.firstPaymentDate = assetFormItem.firstPaymentDate
                ? DateService.formatDate(assetFormItem.firstPaymentDate) : null;
              assetFormItem.nextPaymentDueDate = assetFormItem.nextPaymentDueDate
                ? DateService.formatDate(assetFormItem.nextPaymentDueDate) : null;
              assetFormItem.originalLoanTermMonth = assetFormItem.originalLoanTermMonth
                ? FormatService.formatCleanNumber(assetFormItem.originalLoanTermMonth, false, true) : 0;
              assetFormItem.loanAge = assetFormItem.loanAge
                ? FormatService.formatCleanNumber(assetFormItem.loanAge, false, true) : 0;
              assetFormItem.remainingTerm = assetFormItem.remainingTerm
                ? FormatService.formatCleanNumber(assetFormItem.remainingTerm, false, true) : 0;
              assetFormItem.amortizationPeriodMonths = assetFormItem.amortizationPeriodMonths
                ? FormatService.formatCleanNumber(assetFormItem.amortizationPeriodMonths, false, true) : 0;
              assetFormItem.remainingAmortizationPeriod = assetFormItem.remainingAmortizationPeriod
                ? FormatService.formatCleanNumber(assetFormItem.remainingAmortizationPeriod, false, true) : 0;
              assetFormItem.interestOnlyPeriod = assetFormItem.interestOnlyPeriod
                ? FormatService.formatCleanNumber(assetFormItem.interestOnlyPeriod, false, true) : 0;
              assetFormItem.remainingInterestOnlyPeriod = assetFormItem.remainingInterestOnlyPeriod
                ? FormatService.formatCleanNumber(assetFormItem.remainingInterestOnlyPeriod, false, true) : 0;
              assetFormItem.lienPosition = assetFormItem.lienPosition
                ? FormatService.formatCleanNumber(assetFormItem.lienPosition, false, true) : 0;
              assetFormItem.currentCreditLetterAmount = assetFormItem.currentCreditLetterAmount
                ? FormatService.formatCleanNumber(assetFormItem.currentCreditLetterAmount) : 0;
              assetFormItem.currentCreditLetterCreditExpirationDate = (
                assetFormItem.currentCreditLetterCreditExpirationDate
                  ? DateService.formatDate(assetFormItem.currentCreditLetterCreditExpirationDate) : null
              );
              assetFormItem.interestReservesAtOrigination = assetFormItem.interestReservesAtOrigination
                ? FormatService.formatCleanNumber(assetFormItem.interestReservesAtOrigination) : 0;
              assetFormItem.taxInsuranceEscrowsAtOrigination = assetFormItem.taxInsuranceEscrowsAtOrigination
                ? FormatService.formatCleanNumber(assetFormItem.taxInsuranceEscrowsAtOrigination) : 0;
              assetFormItem.capitalExpenditureReservesAtOrigination = (
                assetFormItem.capitalExpenditureReservesAtOrigination
                  ? FormatService.formatCleanNumber(assetFormItem.capitalExpenditureReservesAtOrigination) : 0
              );
              assetFormItem.otherEscrowAndReservesAtOrigination = assetFormItem.otherEscrowAndReservesAtOrigination
                ? FormatService.formatCleanNumber(assetFormItem.otherEscrowAndReservesAtOrigination) : 0;
              assetFormItem.currentInterestReserves = assetFormItem.currentInterestReserves
                ? FormatService.formatCleanNumber(assetFormItem.currentInterestReserves) : 0;
              assetFormItem.currentTaxInsuranceEscrows = assetFormItem.currentTaxInsuranceEscrows
                ? FormatService.formatCleanNumber(assetFormItem.currentTaxInsuranceEscrows) : 0;
              assetFormItem.currentCapitalExpenditureEscrows = assetFormItem.currentCapitalExpenditureEscrows
                ? FormatService.formatCleanNumber(assetFormItem.currentCapitalExpenditureEscrows) : 0;
              assetFormItem.currentOtherEscrowsAndReserves = assetFormItem.currentOtherEscrowsAndReserves
                ? FormatService.formatCleanNumber(assetFormItem.currentOtherEscrowsAndReserves) : 0;
              assetFormItem.initialAppraisalDate = assetFormItem.initialAppraisalDate
                ? DateService.formatDate(assetFormItem.initialAppraisalDate) : null;
              assetFormItem.stabilizedAppraisedNoi = assetFormItem.stabilizedAppraisedNoi
                ? FormatService.formatCleanNumber(assetFormItem.stabilizedAppraisedNoi) : 0;
              assetFormItem.stabilizedOccupancy = assetFormItem.stabilizedOccupancy
                ? FormatService.formatCleanNumber(assetFormItem.stabilizedOccupancy, true) : 0;
              assetFormItem.stabilizedCapitalizationRate = assetFormItem.stabilizedCapitalizationRate
                ? FormatService.formatCleanNumber(assetFormItem.stabilizedCapitalizationRate, true) : 0;
              assetFormItem.mostRecentAppraisalDate = assetFormItem.mostRecentAppraisalDate
                ? DateService.formatDate(assetFormItem.mostRecentAppraisalDate) : null;
              assetFormItem.atCloseRevenue = assetFormItem.atCloseRevenue
                ? FormatService.formatCleanNumber(assetFormItem.atCloseRevenue) : 0;
              assetFormItem.atCloseExpenses = assetFormItem.atCloseExpenses
                ? FormatService.formatCleanNumber(assetFormItem.atCloseExpenses) : 0;
              assetFormItem.atCloseNoi = assetFormItem.atCloseNoi
                ? FormatService.formatCleanNumber(assetFormItem.atCloseNoi) : 0;
              assetFormItem.atCloseDebtYieldNoi = assetFormItem.atCloseDebtYieldNoi
                ? FormatService.formatCleanNumber(assetFormItem.atCloseDebtYieldNoi, true) : 0;
              assetFormItem.occupancyAtOrigination = assetFormItem.occupancyAtOrigination
                ? FormatService.formatCleanNumber(assetFormItem.occupancyAtOrigination, true) : 0;
              assetFormItem.atCloseNoiAndOccupancyDate = assetFormItem.atCloseNoiAndOccupancyDate
                ? DateService.formatDate(assetFormItem.atCloseNoiAndOccupancyDate) : null;
              assetFormItem.firstExtendedMaturityDate = assetFormItem.firstExtendedMaturityDate
                ? DateService.formatDate(assetFormItem.firstExtendedMaturityDate) : null;
              assetFormItem.secondExtendedMaturityDate = assetFormItem.secondExtendedMaturityDate
                ? DateService.formatDate(assetFormItem.secondExtendedMaturityDate) : null;
              assetFormItem.thirdExtendedMaturityDate = assetFormItem.thirdExtendedMaturityDate
                ? DateService.formatDate(assetFormItem.thirdExtendedMaturityDate) : null;

              if (assetFormItem.seller && !Object.keys(assetFormItem.seller).length) {
                delete assetFormItem.seller;
              }

              if (assetPhotoFiles[assetId]) {
                assetFormItem.mainPhoto = assetPhotoFiles[assetId];
              } else {
                delete assetFormItem.mainPhoto;
              }

              const assetOptions: AssetFieldInput[] = [];
              if (assetFormItem.options && Object.keys(assetFormItem.options).length) {
                Object.keys(assetFormItem.options).forEach((assetFieldIndex: string): void => {
                  assetOptions.push({ ...assetFormItem.options[assetFieldIndex] });
                });
              }
              assetFormItem.options = assetOptions;

              if (assetFormItem && Object.keys(assetFormItem).length) {
                portfolioItem.assets.push(assetFormItem);
              }
            }
          });

          assetList.push(portfolioItem);
        }
      });
    }

    const portfolioGroupList: PortfolioGroupInput[] = [];
    if (portfolioGroupListFormData && Object.keys(portfolioGroupListFormData).length) {
      Object.keys(portfolioGroupListFormData).forEach((portfolioGroupIndex: string): void => {
        const portfolioGroupFormItem: FormValueItem = {
          ...portfolioGroupListFormData[portfolioGroupIndex],
        };

        if (portfolioGroupPhotoFiles[FormService.FORM_KEY_NEW]) {
          portfolioGroupFormItem.photo = portfolioGroupPhotoFiles[FormService.FORM_KEY_NEW];
        } else {
          delete portfolioGroupFormItem.photo;
        }

        const portfolioGroupItem: PortfolioGroupInput = portfolioGroupFormItem;

        portfolioGroupList.push(portfolioGroupItem);
      });
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveAssetListData({
        assetListForm: {
          assetList,
          portfolioGroupList,
        },
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, page, true),
      }, (response: ActionResponse): void => {
        if (isNewAsset) {
          openExpansionGroups();
        }

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        setInlineSaveAssetId(null);
        setInlineSavePortfolioId(null);
        setInlineSaveIsConfirm(null);

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, isNewAsset, isNewPortfolio, isNewPortfolioGroup, assetPhotoFiles, portfolioIdsFilter,
    portfolioGroupIdsFilter, isAssetModalOpen, isPortfolioModalOpen, isPortfolioGroupModalOpen, assetFieldNewItems,
    inlineSaveAssetId, inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, page, assetListData,
    selectedRowsDefault, isEditMode, initialFormValues,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, isNewAsset, isNewPortfolio, isNewPortfolioGroup, assetPhotoFiles, portfolioIdsFilter,
    portfolioGroupIdsFilter, isAssetModalOpen, isPortfolioModalOpen, assetFieldNewItems, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, page, assetListData, selectedRowsDefault, isEditMode,
    initialFormValues, formValues, withFormErrors,
  ]);

  const onInlineSave: Function = useCallback((portfolioId: number, assetId: number): void => {
    setInlineSaveAssetId(assetId);
    setInlineSavePortfolioId(portfolioId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onInlineCancel: Function = useCallback((portfolioId: number, assetId: number): void => {
    setInlineSaveAssetId(assetId);
    setInlineSavePortfolioId(portfolioId);
    setInlineSaveIsConfirm(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onPortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds: number[], portfolioFilterIds: number[],
  ): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (portfolioFilterIds && portfolioFilterIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_IDS] = portfolioFilterIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_IDS];
    }

    if (portfolioFilterGroupIds && portfolioFilterGroupIds.length) {
      queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS] = portfolioFilterGroupIds.join(SIGN_DOT);
    } else {
      delete queryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS];
    }

    navigate({
      pathname: ROUTE_ASSET_LIST,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams]);

  const onBeforePortfolioFilterValuesChange: Function = useCallback((
    portfolioFilterGroupIds?: number[], portfolioFilterIds?: number[],
  ): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastPortfolioGroupIdsValue(portfolioFilterGroupIds || []);
      setLastPortfolioIdsValue(portfolioFilterIds || []);
      setLastLocation(undefined);

      return false;
    }

    return true;
  }, [isFormChanged]);

  const openAssetDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    assetId: number,
  ): void => {
    setAssetActiveIds(assetId ? [assetId] : (selectedRows && Object.values(selectedRows)
      .reduce((resultAssetItemIds: number[], assetItemIds: number[]): number[] => (
        [...resultAssetItemIds, ...assetItemIds]
      ), [])));
    setIsAssetDeleteConfirmModalOpen(true);
  }, [selectedRows]);

  const closeAssetDeleteConfirmModal: Function = useCallback((): void => {
    setIsAssetDeleteConfirmModalOpen(false);
    setAssetActiveIds([]);
    setConfirmModalErrorMessage(null);
  }, []);

  const openPortfolioDeleteConfirmModal: Function = useCallback((portfolioId: number): void => {
    setPortfolioActiveId(portfolioId);
    setIsPortfolioDeleteConfirmModalOpen(true);
  }, []);

  const closePortfolioDeleteConfirmModal: Function = useCallback((): void => {
    setIsPortfolioDeleteConfirmModalOpen(false);
    setPortfolioActiveId(null);
    setConfirmModalErrorMessage(null);
  }, []);

  const onInlineGroupEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, portfolioId: number,
  ): void => {
    setPortfolioActiveIds([...(portfolioActiveIds || []), portfolioId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, portfolioActiveIds]);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: ASSET_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, assetId: number): void => {
        if (assetId) {
          openAssetDeleteConfirmModal(event, assetId);
        }
      },
    },
  ], [selectedRows]);

  const actionGroupMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <EditIcon />,
      text: 'Edit',
      permission: PORTFOLIO_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, portfolioId: number): void => {
        if (portfolioId) {
          onInlineGroupEditModeButtonClick(event, portfolioId);
        }
      },
    },
    {
      id: 2,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: PORTFOLIO_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, portfolioId: number): void => {
        if (portfolioId) {
          openPortfolioDeleteConfirmModal(portfolioId);
        }
      },
    },
  ], []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const openGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onDealClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
    });

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, assetId: number,
  ): void => {
    setAssetActiveIds([...(assetActiveIds || []), assetId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, assetActiveIds]);

  const tableAssetListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      group: 'other',
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      component: (asset: Asset): ReactElement => (
        <TableCellShort isTextCenter>
          {
            isEditMode && (
              <Image
                isCentered
                text={asset.name}
                link={asset.mainPhotoThumbnail || asset.mainPhoto || AssetDefaultImage}
              />
            )
          }
        </TableCellShort>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 2,
      name: 'name',
      title: 'Name',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      isSticky: !isCustomizeMode,
      component: (asset: Asset): ReactElement => (
        <TableCell
          isSticky={!isCustomizeMode}
          isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)}
        >
          <FlexContainer>
            {
              !isEditMode && (
                <Image
                  isCentered
                  text={asset.name}
                  link={asset.mainPhotoThumbnail || asset.mainPhoto || AssetDefaultImage}
                />
              )
            }
            <PaddedFlexContainer>
              <FilteredLink
                link={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                  ? `${ROUTE_ASSET}/${asset.id}` : null}
                permission={ASSET_VIEW}
              >
                {asset.name}
              </FilteredLink>
            </PaddedFlexContainer>
          </FlexContainer>
        </TableCell>
      ),
      summaryComponent: (): ReactElement => (
        <TableCell isSticky={!isCustomizeMode} isStickyLeft={!isCustomizeMode}>
          Total / Average
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'portfolio.name',
      title: 'Portfolio',
      group: 'locationInformation',
      isSortable: false,
      isNumber: false,
      isRequired: isEditMode,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.portfolio && asset.portfolio.name}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 4,
      name: 'lastDeal.name',
      title: 'Linked Deal',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {
            asset.lastDeal && (
              <ChipLink
                isDisabled={isEditMode || isCustomizeMode || isExportMode || isSelectMode}
                label={asset.lastDeal.name}
                imageLink={asset.lastDeal.mainPhotoThumbnail || asset.lastDeal.mainPhoto || AssetDefaultImage}
                permission={DEAL_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onDealClick(event, asset.lastDeal.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 5,
      name: 'statusText',
      title: 'Status',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.statusText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 6,
      name: 'streetAddress',
      title: 'Address',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCellExtended isText isEditMode={isEditMode}>
          <FilteredLink
            isExternal
            link={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
            && asset.address && asset.address.url
              ? asset.address.url : null}
          >
            {FormatService.formatStringAddress(asset.streetAddress, asset.location, asset.postalCode)}
          </FilteredLink>
        </TableCellExtended>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 7,
      name: 'addressDetails',
      title: 'Address Details',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {FormatService.formatLimitedString(asset.addressDetails, FormatService.TEXT_LIMIT_TABLE)}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 8,
      name: 'msaLocation',
      title: 'MSA Location',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.msaLocation}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 9,
      name: 'size',
      title: 'Size',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.size && asset.sizeType && `${asset.size} ${asset.sizeType && asset.sizeType.title}`}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.size && assetSummaryData.sizeType && assetSummaryData.sizeType.title
            && `${assetSummaryData.size} ${assetSummaryData.sizeType.title}`}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'amount',
      title: 'Total Capitalization',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.amount}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.amount}
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'value',
      title: 'Total Value',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.value}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.value}
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'loanAmount',
      title: 'Loan Amount',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.loanAmount}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.loanAmount}
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'ltv',
      title: 'LTV',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.ltv}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.ltv}
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'parcelNumber',
      title: 'Location Property Identifier',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.parcelNumber}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 15,
      name: 'submarket',
      title: 'Submarket',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.submarket}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 16,
      name: 'lotSize',
      title: 'Lot Size',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.lotSize}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 17,
      name: 'assetOwnershipStructure',
      title: 'Ownership Structure',
      group: 'other',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.assetOwnershipStructureText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 18,
      name: 'affordableUnits',
      title: 'Affordable Units',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.affordableUnits}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 19,
      name: 'asOfRightZoning',
      title: 'As-of-Right Zoning',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {FormatService.formatBooleanValue(asset.asOfRightZoning, true)}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 20,
      name: 'cost',
      title: 'Cost',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.cost}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 21,
      name: 'occupancy',
      title: 'Occupancy',
      group: 'other',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.occupancy}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 22,
      name: 'currentNoi',
      title: 'Current NOI',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 23,
      name: 'ttmNoi',
      title: 'TTM NOI',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.ttmNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 24,
      name: 'runRateNoi',
      title: 'Run Rate NOI',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.runRateNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 25,
      name: 'forwardNoi',
      title: 'Forward NOI',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.forwardNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 26,
      name: 'maturityDate',
      title: 'Maturity Date',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedMaturityDate: Date | null = FormatService.formatParsedDate(asset.maturityDate);

        return (
          <TableCell>
            {parsedMaturityDate && FormatService.formatDateString(parsedMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 27,
      name: 'lastDeal.capitalizationSummaryData.sources.debt.amount',
      title: 'Total Debt',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.capitalizationSummaryData
            && asset.lastDeal.capitalizationSummaryData.sources && asset.lastDeal.capitalizationSummaryData.sources.debt
            && asset.lastDeal.capitalizationSummaryData.sources.debt.amount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 28,
      name: 'lastDeal.capitalizationSummaryData.sources.equity.amount',
      title: 'Total Equity',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.capitalizationSummaryData
            && asset.lastDeal.capitalizationSummaryData.sources
            && asset.lastDeal.capitalizationSummaryData.sources.equity
            && asset.lastDeal.capitalizationSummaryData.sources.equity.amount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 29,
      name: 'lastDeal.capitalizationSummaryData.sources.generalPartner.amount',
      title: 'Total GP Equity',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.capitalizationSummaryData
            && asset.lastDeal.capitalizationSummaryData.sources
            && asset.lastDeal.capitalizationSummaryData.sources.generalPartner
            && asset.lastDeal.capitalizationSummaryData.sources.generalPartner.amount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 30,
      name: 'sellers.fullName',
      title: 'Sellers',
      group: 'other',
      isSortable: false,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {
            asset.sellers && !!asset.sellers.length && asset.sellers.map((contactItem: Contact): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, contactItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 31,
      name: 'lastDeal.lenders.fullName',
      title: 'Lender',
      group: 'other',
      isSortable: false,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {
            asset.lastDeal && asset.lastDeal.lenders && !!asset.lastDeal.lenders.length
            && asset.lastDeal.lenders.map((contactItem: Contact): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, contactItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 32,
      name: 'lastDeal.receivedDate',
      title: 'Acquisition Date',
      group: 'other',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell>
          {asset.lastDeal && asset.lastDeal.receivedDate}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 33,
      name: 'portfolioInvestedEquity',
      title: 'Portfolio Invested Equity',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.portfolioInvestedEquity}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.portfolioInvestedEquity}
        </TableCell>
      ),
    },
    {
      id: 34,
      name: 'leveredIrr',
      title: 'Levered IRR',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.leveredIrr}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.leveredIrr}
        </TableCell>
      ),
    },
    {
      id: 35,
      name: 'loanNumber',
      title: 'Loan Number',
      group: 'other',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.loanNumber}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 36,
      name: 'equityMultiple',
      title: 'Equity Multiple',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.equityMultiple}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 37,
      name: 'lastDeal.sponsor',
      title: 'Owner Name',
      group: 'ownershipInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.lastDeal && asset.lastDeal.sponsor && asset.lastDeal.sponsor.fullName}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 38,
      name: 'lastDeal.zfaAmount',
      title: 'ZFA',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.zfaAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 39,
      name: 'lastDeal.gsfAmount',
      title: 'GSF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.gsfAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 40,
      name: 'lastDeal.nsfAmount',
      title: 'NSF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.nsfAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 41,
      name: 'lastDeal.unitsAmount',
      title: 'Units',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.unitsAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 42,
      name: 'lastDeal.keysAmount',
      title: 'Keys',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.keysAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 43,
      name: 'lastDeal.acresAmount',
      title: 'Acres',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.acresAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 44,
      name: 'lastDeal.lotsfAmount',
      title: 'Lot SF',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lastDeal && asset.lastDeal.lotsfAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 45,
      name: 'leedDesignation',
      title: 'LEED Designation',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.leedDesignation}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 46,
      name: 'ownerEntityName',
      title: 'Owner Entity Name',
      group: 'ownershipInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.ownerEntityName}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 47,
      name: 'futureNoi',
      title: 'Future NOI',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.futureNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 48,
      name: 'investmentEntityName',
      title: 'Investment Entity Name',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.investmentEntityName}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 49,
      name: 'investmentAmountCommitted',
      title: 'Investment Amount Committed',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.investmentAmountCommitted}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 50,
      name: 'investmentAmountUnfunded',
      title: 'Investment Amount Unfunded',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.investmentAmountUnfunded}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 51,
      name: 'investmentAmountFunded',
      title: 'Investment Amount Funded',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.investmentAmountFunded}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 52,
      name: 'currentMarketValue',
      title: 'Current Market Value',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentMarketValue}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 53,
      name: 'closeDate',
      title: 'Close Date',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedCloseDate: Date | null = FormatService.formatParsedDate(asset.closeDate);

        return (
          <TableCell>
            {parsedCloseDate && FormatService.formatDateString(parsedCloseDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 54,
      name: 'extensions',
      title: 'Extensions',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.extensions}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 55,
      name: 'amountInvested',
      title: 'Amount Invested',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.amountInvested}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 56,
      name: 'projectedStabilizedNoi',
      title: 'Projected Stabilized NOI',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedStabilizedNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 57,
      name: 'goingInCapRate',
      title: 'Going In Cap Rate',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.goingInCapRate}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 58,
      name: 'untrendedYieldToCost',
      title: 'Untrended Yield To Cost',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.untrendedYieldToCost}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 59,
      name: 'projectedUnleveredIrr',
      title: 'Projected Unlevered IRR',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedUnleveredIrr}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 60,
      name: 'projectedUnleveredEquityMultiple',
      title: 'Projected Unlevered Equity Multiple',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedUnleveredEquityMultiple}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 61,
      name: 'projectedUnleveredNetProfit',
      title: 'Projected Unlevered Net Profit',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedUnleveredNetProfit}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 62,
      name: 'projectedLeveredIrr',
      title: 'Projected Levered IRR',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedLeveredIrr}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 63,
      name: 'projectedLeveredEquityMultiple',
      title: 'Projected Levered Equity Multiple',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedLeveredEquityMultiple}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 64,
      name: 'projectedLeveredNetProfit',
      title: 'Projected Levered Net Profit',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.projectedLeveredNetProfit}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 65,
      name: 'seniorLenderName',
      title: 'Senior Lender Name',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.seniorLenderName}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 66,
      name: 'seniorLoanAmount',
      title: 'Senior Loan Amount',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.seniorLoanAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 67,
      name: 'seniorLoanClosingDate',
      title: 'Senior Loan Closing Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedSeniorLoanClosingDate: Date | null = FormatService.formatParsedDate(asset.seniorLoanClosingDate);

        return (
          <TableCell>
            {parsedSeniorLoanClosingDate && FormatService.formatDateString(parsedSeniorLoanClosingDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 68,
      name: 'seniorLoanMaturityDate',
      title: 'Senior Loan Maturity Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedSeniorLoanMaturityDate: Date | null = FormatService.formatParsedDate(asset.seniorLoanMaturityDate);

        return (
          <TableCell>
            {parsedSeniorLoanMaturityDate && FormatService.formatDateString(parsedSeniorLoanMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 69,
      name: 'seniorLoanInterestRate',
      title: 'Senior Loan Interest Rate',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.seniorLoanInterestRateText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 70,
      name: 'seniorLoanInterestSpread',
      title: 'Senior Loan Interest Spread',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.seniorLoanInterestSpread}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 71,
      name: 'seniorLoanLtv',
      title: 'Senior Loan LTV',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.seniorLoanLtv}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 72,
      name: 'seniorLoanLtc',
      title: 'Senior Loan LTC',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.seniorLoanLtc}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 73,
      name: 'mezzanineLenderName',
      title: 'Mezzanine Lender Name',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.mezzanineLenderName}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 74,
      name: 'mezzanineLoanAmount',
      title: 'Mezzanine Loan Amount',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.mezzanineLoanAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 75,
      name: 'mezzanineLoanClosingDate',
      title: 'Mezzanine Loan Closing Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedMezzanineLoanClosingDate: Date | null = FormatService.formatParsedDate(
          asset.mezzanineLoanClosingDate,
        );

        return (
          <TableCell>
            {parsedMezzanineLoanClosingDate && FormatService.formatDateString(parsedMezzanineLoanClosingDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 76,
      name: 'mezzanineLoanMaturityDate',
      title: 'Mezzanine Loan Maturity Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedMezzanineLoanMaturityDate: Date | null = FormatService.formatParsedDate(
          asset.mezzanineLoanMaturityDate,
        );

        return (
          <TableCell>
            {parsedMezzanineLoanMaturityDate && FormatService.formatDateString(parsedMezzanineLoanMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 77,
      name: 'mezzanineLoanInterestRate',
      title: 'Mezzanine Loan Interest Rate',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.mezzanineLoanInterestRateText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 78,
      name: 'mezzanineLoanInterestSpread',
      title: 'Mezzanine Loan Interest Spread',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.mezzanineLoanInterestSpread}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 79,
      name: 'mezzanineLoanLtv',
      title: 'Mezzanine Loan LTV',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.mezzanineLoanLtv}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 80,
      name: 'mezzanineLoanLtc',
      title: 'Mezzanine Loan LTC',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.mezzanineLoanLtc}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 81,
      name: 'blendedLoanInterestRate',
      title: 'Blended Loan Interest Rate',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.blendedLoanInterestRateText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 82,
      name: 'blendedLoanInterestSpread',
      title: 'Blended Loan Interest Spread',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.blendedLoanInterestSpread}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 83,
      name: 'yardiAssetId',
      title: 'Yardi Asset ID',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.yardiAssetId}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 84,
      name: 'yardiAppraisedValue',
      title: 'Appraised Value (Yardi)',
      group: 'propertyCapitalization',
      isSortable: false,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiAppraisedValue}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiAppraisedValue}
        </TableCell>
      ),
    },
    {
      id: 85,
      name: 'yardiLoanBalance',
      title: 'Loan Balance (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiLoanBalance}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiLoanBalance}
        </TableCell>
      ),
    },
    {
      id: 86,
      name: 'yardiSeniorLoan',
      title: 'Senior Loan (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiSeniorLoan}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiSeniorLoan}
        </TableCell>
      ),
    },
    {
      id: 87,
      name: 'yardiMarketValueLoan',
      title: 'Market Value Loan (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiMarketValueLoan}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiMarketValueLoan}
        </TableCell>
      ),
    },
    {
      id: 88,
      name: 'yardiLoanClose',
      title: 'Loan Close (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell>
          {asset.yardiLoanClose}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 89,
      name: 'yardiLoanMaturity',
      title: 'Loan Maturity (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell>
          {asset.yardiLoanMaturity}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 90,
      name: 'yardiLoanCommitment',
      title: 'Loan Commitment (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiLoanCommitment}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiLoanCommitment}
        </TableCell>
      ),
    },
    {
      id: 91,
      name: 'yardiLoanContribution',
      title: 'Loan Contribution (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiLoanContribution}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiLoanContribution}
        </TableCell>
      ),
    },
    {
      id: 92,
      name: 'yardiUnfundedLoan',
      title: 'Unfunded Loan (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiUnfundedLoan}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiUnfundedLoan}
        </TableCell>
      ),
    },
    {
      id: 93,
      name: 'yardiManagementFeePercent',
      title: 'Management Fee Percent (Yardi)',
      group: 'managementFeeInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiManagementFeePercent}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiManagementFeePercent}
        </TableCell>
      ),
    },
    {
      id: 94,
      name: 'yardiCurrentManagementFee',
      title: 'Current Management Fee (Yardi)',
      group: 'managementFeeInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiCurrentManagementFee}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiCurrentManagementFee}
        </TableCell>
      ),
    },
    {
      id: 95,
      name: 'yardiCurrentInterest',
      title: 'Current Interest (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiCurrentInterest}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiCurrentInterest}
        </TableCell>
      ),
    },
    {
      id: 96,
      name: 'yardiItdInterest',
      title: 'ITD Interest (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiItdInterest}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiItdInterest}
        </TableCell>
      ),
    },
    {
      id: 97,
      name: 'yardiCurrentAmortization',
      title: 'Current Amortization (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiCurrentAmortization}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiCurrentAmortization}
        </TableCell>
      ),
    },
    {
      id: 98,
      name: 'yardiItdAmortization',
      title: 'ITD Amortization (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiItdAmortization}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiItdAmortization}
        </TableCell>
      ),
    },
    {
      id: 99,
      name: 'yardiIrrOnLoan',
      title: 'IRR On Loan (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiIrrOnLoan}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiIrrOnLoan}
        </TableCell>
      ),
    },
    {
      id: 100,
      name: 'yardiEquityMultiple',
      title: 'Equity Multiple (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiEquityMultiple}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiEquityMultiple}
        </TableCell>
      ),
    },
    {
      id: 101,
      name: 'yardiGainOnLoan',
      title: 'Gain On Loan (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiGainOnLoan}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiGainOnLoan}
        </TableCell>
      ),
    },
    {
      id: 102,
      name: 'yardiLtv',
      title: 'LTV (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiLtv}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiLtv}
        </TableCell>
      ),
    },
    {
      id: 103,
      name: 'yardiCurrentSofr',
      title: 'Current SOFR (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiCurrentSofr}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiCurrentSofr}
        </TableCell>
      ),
    },
    {
      id: 104,
      name: 'yardiLoanSpread',
      title: 'Loan Spread (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiLoanSpread}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiLoanSpread}
        </TableCell>
      ),
    },
    {
      id: 105,
      name: 'yardiCurrentRate',
      title: 'Current Rate (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiCurrentRate}
        </TableCell>
      ),
      summaryComponent: (assetSummaryData: Asset): ReactElement => (
        <TableCell isNumber>
          {assetSummaryData.yardiCurrentRate}
        </TableCell>
      ),
    },
    {
      id: 106,
      name: 'yardiFirstExtensionTerm',
      title: '1st Extension Term (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiFirstExtensionTerm}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 107,
      name: 'yardiSecondExtensionTerm',
      title: '2nd Extension Term (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiSecondExtensionTerm}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 108,
      name: 'yardiBorrower',
      title: 'Borrower (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiBorrower}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 109,
      name: 'yardiSponsor',
      title: 'Sponsor (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiSponsor}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 110,
      name: 'yardiSponsorAdditional',
      title: '2nd Sponsor (Yardi)',
      group: 'other',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yardiSponsorAdditional}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 111,
      name: 'notePrincipalBalanceAtOrigination',
      title: 'Note Principal Balance at Origination',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.notePrincipalBalanceAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 112,
      name: 'accruedInterest',
      title: 'Accrued Interest',
      group: 'investmentPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.accruedInterest}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 113,
      name: 'marketType',
      title: 'Market Type',
      group: 'locationInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.marketType}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 114,
      name: 'yearBuilt',
      title: 'Year Built',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yearBuilt}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 115,
      name: 'yearRenovated',
      title: 'Year Renovated',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.yearRenovated}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 116,
      name: 'firstPaymentDate',
      title: 'First Payment Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedFirstPaymentDate: Date | null = FormatService.formatParsedDate(asset.firstPaymentDate);

        return (
          <TableCell>
            {parsedFirstPaymentDate && FormatService.formatDateString(parsedFirstPaymentDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 117,
      name: 'nextPaymentDueDate',
      title: 'Next Payment Due Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedNextPaymentDueDate: Date | null = FormatService.formatParsedDate(asset.nextPaymentDueDate);

        return (
          <TableCell>
            {parsedNextPaymentDueDate && FormatService.formatDateString(parsedNextPaymentDueDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 118,
      name: 'originalLoanTermMonth',
      title: 'Original Loan Term Month',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.originalLoanTermMonth}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 119,
      name: 'loanAge',
      title: 'Loan Age',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.loanAge}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 120,
      name: 'remainingTerm',
      title: 'Remaining Term',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.remainingTerm}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 121,
      name: 'amortizationPeriodMonths',
      title: 'Amortization Period Months',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.amortizationPeriodMonths}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 122,
      name: 'remainingAmortizationPeriod',
      title: 'Remaining Amortization Period',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.remainingAmortizationPeriod}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 123,
      name: 'paymentStringYear',
      title: 'Payment String Year',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.paymentStringYear}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 124,
      name: 'amortizationType',
      title: 'Amortization Type',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.amortizationType}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 125,
      name: 'interestOnlyPeriod',
      title: 'Interest Only Period',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.interestOnlyPeriod}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 126,
      name: 'remainingInterestOnlyPeriod',
      title: 'Remaining Interest Only Period',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.remainingInterestOnlyPeriod}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 127,
      name: 'interestAccrualMethod',
      title: 'Interest Accrual Method',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.interestAccrualMethod}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 128,
      name: 'primaryLoanPurpose',
      title: 'Primary Loan Purpose',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.primaryLoanPurpose}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 129,
      name: 'underlyingLoanType',
      title: 'Underlying Loan Type',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.underlyingLoanType}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 130,
      name: 'lienPosition',
      title: 'Lien Position',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.lienPosition}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 131,
      name: 'primaryCollateral',
      title: 'Primary Collateral',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.primaryCollateral}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 132,
      name: 'securityInterest',
      title: 'Security Interest',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.securityInterest}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 133,
      name: 'groundLease',
      title: 'Ground Lease',
      group: 'propertyInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {FormatService.formatBooleanValue(asset.groundLease, true)}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 134,
      name: 'prepaymentProvisions',
      title: 'Prepayment Provisions',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.prepaymentProvisions}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 135,
      name: 'remainingPrepaymentProvisions',
      title: 'Remaining Prepayment Provisions',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.remainingPrepaymentProvisions}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 136,
      name: 'currentCreditLetterAmount',
      title: 'Current Credit Letter Amount',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentCreditLetterAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 137,
      name: 'currentCreditLetterCreditExpirationDate',
      title: 'Current Credit Letter Credit Expiration Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedCurrentCreditLetterCreditExpirationDate: Date | null = FormatService.formatParsedDate(
          asset.currentCreditLetterCreditExpirationDate,
        );

        return (
          <TableCell>
            {
              parsedCurrentCreditLetterCreditExpirationDate
              && FormatService.formatDateString(parsedCurrentCreditLetterCreditExpirationDate)
            }
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 138,
      name: 'interestReservesAtOrigination',
      title: 'Interest Reserves at Origination',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.interestReservesAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 139,
      name: 'taxInsuranceEscrowsAtOrigination',
      title: 'Tax Insurance Escrows at Origination',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.taxInsuranceEscrowsAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 140,
      name: 'capitalExpenditureReservesAtOrigination',
      title: 'Cap Ex Reserves at Origination',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.capitalExpenditureReservesAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 141,
      name: 'otherEscrowAndReservesAtOrigination',
      title: 'Other Escrow and Reserves at Origination',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.otherEscrowAndReservesAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 142,
      name: 'currentInterestReserves',
      title: 'Current Interest Reserves',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentInterestReserves}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 143,
      name: 'currentTaxInsuranceEscrows',
      title: 'Current Tax Insurance Escrows',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentTaxInsuranceEscrows}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 144,
      name: 'currentCapitalExpenditureEscrows',
      title: 'Current Cap Ex Escrows',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentCapitalExpenditureEscrows}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 145,
      name: 'currentOtherEscrowsAndReserves',
      title: 'Current Other Escrows and Reserves',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.currentOtherEscrowsAndReserves}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 146,
      name: 'initialAppraisalDate',
      title: 'Initial Appraisal Date',
      group: 'investmentInformation',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedInitialAppraisalDate: Date | null = FormatService.formatParsedDate(asset.initialAppraisalDate);

        return (
          <TableCell>
            {parsedInitialAppraisalDate && FormatService.formatDateString(parsedInitialAppraisalDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 147,
      name: 'stabilizedAppraisedNoi',
      title: 'Stabilized Appraised NOI',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.stabilizedAppraisedNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 148,
      name: 'stabilizedOccupancy',
      title: 'Stabilized Occupancy',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.stabilizedOccupancy}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 149,
      name: 'stabilizedCapitalizationRate',
      title: 'Stabilized Capitalization Rate',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.stabilizedCapitalizationRate}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 150,
      name: 'mostRecentAppraisalDate',
      title: 'Most Recent Appraisal Date',
      group: 'propertyCapitalization',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedMostRecentAppraisalDate: Date | null = FormatService.formatParsedDate(
          asset.mostRecentAppraisalDate,
        );

        return (
          <TableCell>
            {parsedMostRecentAppraisalDate && FormatService.formatDateString(parsedMostRecentAppraisalDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 151,
      name: 'atCloseRevenue',
      title: 'At Close Revenue',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.atCloseRevenue}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 152,
      name: 'atCloseExpenses',
      title: 'At Close Expenses',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.atCloseExpenses}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 153,
      name: 'atCloseNoi',
      title: 'At Close NOI',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.atCloseNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 154,
      name: 'atCloseDebtYieldNoi',
      title: 'At Close Debt Yield NOI',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.atCloseDebtYieldNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 155,
      name: 'atCloseDscrNoi',
      title: 'At Close DSCR NOI',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.atCloseDscrNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 156,
      name: 'atCloseNcf',
      title: 'At Close NCF',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.atCloseNcf}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 157,
      name: 'atCloseDebtYieldNcf',
      title: 'At Close Debt Yield NCF',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.atCloseDebtYieldNcf}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 158,
      name: 'atCloseDscrNcf',
      title: 'At Close DSCR NCF',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.atCloseDscrNcf}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 159,
      name: 'occupancyAtOrigination',
      title: 'Occupancy at Origination',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: true,
      component: (asset: Asset): ReactElement => (
        <TableCell isNumber>
          {asset.occupancyAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 160,
      name: 'atCloseNoiAndOccupancyDate',
      title: 'At Close NOI and Occupancy Date',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedAtCloseNoiAndOccupancyDate: Date | null = FormatService.formatParsedDate(
          asset.atCloseNoiAndOccupancyDate,
        );

        return (
          <TableCell>
            {parsedAtCloseNoiAndOccupancyDate && FormatService.formatDateString(parsedAtCloseNoiAndOccupancyDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 161,
      name: 'stabilizedNoiAtOrigination',
      title: 'Stabilized NOI at Origination',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.stabilizedNoiAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 162,
      name: 'stabilizedNcfAtOrigination',
      title: 'Stabilized NCF at Origination',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {asset.stabilizedNcfAtOrigination}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 163,
      name: 'firstExtendedMaturityDate',
      title: '1st Extended Maturity Date',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedFirstExtendedMaturityDate: Date | null = FormatService.formatParsedDate(
          asset.firstExtendedMaturityDate,
        );

        return (
          <TableCell>
            {parsedFirstExtendedMaturityDate && FormatService.formatDateString(parsedFirstExtendedMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 164,
      name: 'secondExtendedMaturityDate',
      title: '2nd Extended Maturity Date',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedSecondExtendedMaturityDate: Date | null = FormatService.formatParsedDate(
          asset.secondExtendedMaturityDate,
        );

        return (
          <TableCell>
            {parsedSecondExtendedMaturityDate && FormatService.formatDateString(parsedSecondExtendedMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 165,
      name: 'thirdExtendedMaturityDate',
      title: '3rd Extended Maturity Date',
      group: 'propertyPerformance',
      isSortable: true,
      isNumber: false,
      component: (asset: Asset): ReactElement => {
        const parsedThirdExtendedMaturityDate: Date | null = FormatService.formatParsedDate(
          asset.thirdExtendedMaturityDate,
        );

        return (
          <TableCell>
            {parsedThirdExtendedMaturityDate && FormatService.formatDateString(parsedThirdExtendedMaturityDate)}
          </TableCell>
        );
      },
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 166,
      name: 'types',
      title: 'Asset Classes',
      group: 'propertyInformation',
      isSortable: false,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {
            !!asset.types && !!Object.keys(asset.types).length
            && asset.types.map((assetTypeItem: AssetType): ReactElement => (
              <Chip
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={assetTypeItem.id}
                label={assetTypeItem.title}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 167,
      name: 'tags',
      title: 'Tags',
      group: 'propertyInformation',
      isSortable: false,
      isNumber: false,
      component: (asset: Asset): ReactElement => (
        <TableCell isText>
          {
            !!asset.tags && !!asset.tags.length && asset.tags.map((tagItem: Tag): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={tagItem.id}
                label={tagItem.name}
                permission={SEARCH_RESULT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onTagClick(event, tagItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 168,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isNotFiltered: true,
      isSticky: !isCustomizeMode && !isExportMode && !isSelectMode,
      isStickyRight: !isCustomizeMode && !isExportMode && !isSelectMode,
      component: (asset: Asset): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyRight={!isCustomizeMode && !isExportMode && !isSelectMode}
          onClick={onActionCellClick}
        >
          {
            (!isEditMode || !checkIsAssetActive(asset.id)) && !isExportMode && !isSelectMode && !isCustomizeMode && (
              <CheckAccess permissions={assetEditPermissions}>
                <ActionButton
                  withSmallPadding
                  isHidden={!!isFormSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, asset.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode && (
              <CheckAccess
                permissions={actionMenuItems
                  && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                    actionMenuItem.permission
                  ))}
              >
                <ActionButton
                  withPopup
                  withSmallPadding
                  withFixedSize
                  isHidden={!!isFormSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_ASSET_ID]: asset.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode}
          isStickyRight={!isCustomizeMode}
        />
      ),
    },
  ], [isEditMode, isExportMode, isSelectMode, isCustomizeMode, assetActiveIds, isFormSaving]);

  const tableAssetListFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'locationInformation',
      title: 'Location Info',
    },
    {
      id: 2,
      name: 'propertyInformation',
      title: 'Property Info',
    },
    {
      id: 3,
      name: 'propertyCapitalization',
      title: 'Property Capitalization',
    },
    {
      id: 4,
      name: 'ownershipInformation',
      title: 'Ownership Info',
    },
    {
      id: 5,
      name: 'investmentInformation',
      title: 'Investment Info',
    },
    {
      id: 6,
      name: 'investmentPerformance',
      title: 'Investment Performance',
    },
    {
      id: 7,
      name: 'propertyPerformance',
      title: 'Property Performance',
    },
    {
      id: 8,
      name: 'managementFeeInformation',
      title: 'Management Fee Info',
    },
    {
      id: 9,
      name: 'other',
      title: 'Other',
    },
  ], []);

  const openExportModal: Function = useCallback((): void => {
    if (selectedRows) {
      let assetActiveIdList: number[] = [];
      Object.keys(selectedRows).forEach((portfolioIndex: string): void => {
        const assetItemIds: number[] = selectedRows[portfolioIndex];
        assetActiveIdList = [...assetActiveIdList, ...assetItemIds];
      });

      setAssetActiveIds(assetActiveIdList);
      setIsExportModalOpen(true);
    }
  }, [selectedRows]);

  const closeExportModal: Function = useCallback((): void => {
    setIsExportModalOpen(false);

    onCancelForm();
    closeMessage();
  }, [
    assetListData, selectedRowsDefault, isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles,
    assetFieldNewItems,
  ]);

  const onExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [
    assetListData, selectedRowsDefault, isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles,
    assetFieldNewItems,
  ]);

  const onImportSuccess: Function = useCallback((): void => {
    setPage(PAGE_LIST_DEFAULT);
    dispatch(getAssetListData(getOptions(portfolioIdsFilter, portfolioGroupIdsFilter)));
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onPortfolioDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!portfolioActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deletePortfolio({
        portfolioId: portfolioActiveId,
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, page, true),
      }, (response: ActionResponse): void => {
        setIsPortfolioDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    portfolioActiveId, portfolioIdsFilter, portfolioGroupIdsFilter, page, assetListData, selectedRowsDefault,
    isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId, inlineSavePortfolioId,
    assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles, assetFieldNewItems,
  ]);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!assetActiveIds || !assetActiveIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteAssetList({
        assetIds: assetActiveIds,
        isListItem: true,
        ...getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, page, true),
      }, (response: ActionResponse): void => {
        setIsAssetDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    assetActiveIds, portfolioIdsFilter, portfolioGroupIdsFilter, assetListData, selectedRowsDefault, page,
    isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId, inlineSavePortfolioId,
    portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles, assetFieldNewItems,
  ]);

  const sortTableRows: Function = useCallback((
    portfolioId: number, columnName: string, isNumber?: boolean,
  ): void => {
    setSortParams({
      ...(sortParams || {}),
      [portfolioId]: {
        field: columnName,
        type: sortParams && !!sortParams[portfolioId] && sortParams[portfolioId].field === columnName
        && sortParams[portfolioId].type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
        isNumber,
      },
    });
  }, [sortParams]);

  const openAssetModal: Function = useCallback((): void => {
    setIsNewAsset(true);
    setIsAssetModalOpen(true);

    if (isEditMode) {
      setInlineSaveIsConfirm(null);
      setInlineSaveAssetId(FormService.FORM_KEY_NEW);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
    }
  }, [isEditMode]);

  const openPortfolioModal: Function = useCallback((): void => {
    setIsPortfolioModalOpen(true);
    setIsNewPortfolio(true);
  }, []);

  const openPortfolioGroupModal: Function = useCallback((): void => {
    setIsPortfolioGroupModalOpen(true);
    setIsNewPortfolioGroup(true);
  }, []);

  const portfolioGroupPhotoChange: Function = useCallback((file: File): void => {
    setPortfolioGroupPhotoFiles({
      ...portfolioGroupPhotoFiles,
      [FormService.FORM_KEY_NEW]: file,
    });
  }, [portfolioGroupPhotoFiles]);

  const portfolioGroupPhotoDelete: Function = useCallback((): void => {
    setPortfolioGroupPhotoFiles({});
  }, []);

  const onAssetPhotoChange: Function = useCallback((portfolioId: number, assetId: number, file: File): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    setAssetPhotoFiles({ ...assetPhotoFiles, [assetId]: file });
    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].mainPhoto`, file);
  }, [assetPhotoFiles]);

  const onAssetPhotoDelete: Function = useCallback((portfolioId: number, assetId: number): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);
    const newAssetPhotoFiles: Record<string, File> = {
      ...assetPhotoFiles,
    };

    delete newAssetPhotoFiles[assetId];
    setAssetPhotoFiles(newAssetPhotoFiles);
    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].mainPhoto`, null);
  }, [assetPhotoFiles]);

  const onAssetTypesChange: Function = useCallback((
    portfolioId: number, assetId: number, assetTypeValues: AssetType[],
  ): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].types`, assetTypeValues);
  }, []);

  const onAssetSellersChange: Function = useCallback((
    portfolioId: number, assetId: number, contactValues: Contact,
  ): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].sellerIds`, contactValues);
  }, []);

  const onCreateAssetButtonClick: Function = useCallback((): void => {
    openAssetModal();
  }, []);

  const onCreatePortfolioButtonClick: Function = useCallback((): void => {
    openPortfolioModal();
  }, [isEditMode, assetListData, portfolioGroupList, expandedGroups]);

  const onCreatePortfolioGroupButtonClick: Function = useCallback((): void => {
    openPortfolioGroupModal();
  }, [isEditMode, assetListData, portfolioGroupList, expandedGroups]);

  const onAssetCreate: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSaveAssetId(FormService.FORM_KEY_NEW);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(true);
    } else {
      onSubmitForm();
    }
  }, [
    isEditMode, isSaveDisabled, isNewAsset, isNewPortfolio, assetPhotoFiles, portfolioIdsFilter,
    portfolioGroupIdsFilter, isAssetModalOpen, isPortfolioModalOpen, assetFieldNewItems, initialFormValues,
    inlineSaveAssetId, inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, formValues,
  ]);

  const onPortfolioCreate: Function = useCallback((): void => {
    onSubmitForm();
  }, [
    isSaveDisabled, isNewAsset, isNewPortfolio, assetPhotoFiles, portfolioIdsFilter, portfolioGroupIdsFilter,
    isAssetModalOpen, isPortfolioModalOpen, assetActiveIds, portfolioActiveIds, formValues,
  ]);

  const onAssetModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSaveAssetId(FormService.FORM_KEY_NEW);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      onCancelForm();
    }
  }, [
    isEditMode, assetActiveIds, portfolioActiveIds, assetListData, selectedRowsDefault, isPortfolioModalOpen,
    isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId, inlineSavePortfolioId, initialFormValues,
    assetPhotoFiles, assetFieldNewItems,
  ]);

  const onPortfolioModalCancel: Function = useCallback((): void => {
    onCancelForm();
  }, [
    assetListData, selectedRowsDefault, isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles,
    assetFieldNewItems,
  ]);

  const onAddressSearchCompleted: Function = useCallback((
    addressObject: JsonObject, assetId: number, portfolioId: number,
  ): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].address`, addressObject);
    changeForm(
      `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].location`,
      GoogleMapService.extractLocation(addressObject),
    );
    changeForm(
      `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].postalCode`,
      GoogleMapService.extractPostalCode(addressObject),
    );
    changeForm(
      `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].streetAddress`,
      GoogleMapService.extractStreetAddress(addressObject),
    );

    setAssetAddressList({ ...assetAddressList, [assetId]: addressObject });
  }, [assetAddressList]);

  const onAddressFieldKeyDown: Function = useCallback((assetId: number, portfolioId: number): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    if (assetAddressList && assetAddressList[assetId]) {
      changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].address`, '');
      changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].location`, '');
      changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].postalCode`, '');
      changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].streetAddress`, '');

      setAssetAddressList({ ...assetAddressList, [assetId]: null });
    }
  }, [assetAddressList]);

  const onAssetFieldAddNewItem: Function = useCallback((assetFieldItem: AssetFieldInput): void => {
    const assetId: number = FormService.FORM_KEY_NEW;
    const assetFieldItems: AssetFieldInput[] = assetFieldNewItems[assetId] || [];

    setAssetFieldNewItems({
      ...assetFieldNewItems,
      [assetId]: [...assetFieldItems, assetFieldItem],
    });
  }, [assetFieldNewItems]);

  const onAssetFieldDeleteClick: Function = useCallback((
    portfolioId: number, assetId: number, assetFieldId: number,
  ): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);
    const formKeyAssetField: string = FormService.getIndexedFormFieldKey(assetFieldId);

    if (assetFieldNewItems[assetId] && assetFieldNewItems[assetId].length) {
      assetFieldNewItems[assetId].forEach((assetFieldItem: AssetFieldInput, assetFieldIndex: number): void => {
        if (assetFieldItem && assetFieldItem.id === assetFieldId) {
          delete assetFieldNewItems[assetId][assetFieldIndex];
        }
      });
      setAssetFieldNewItems({
        ...assetFieldNewItems,
        [assetId]: [...assetFieldNewItems[assetId]
          .filter((assetFieldItem: AssetFieldInput): boolean => !!assetFieldItem)],
      });
    }

    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].options[${formKeyAssetField}]`, null);
  }, [assetFieldNewItems]);

  const onSelectAllGroupRows: Function = useCallback((event: React.ChangeEvent, rowGroupId: number): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    assetListData.forEach((portfolioItem: Portfolio): void => {
      if (portfolioItem.id === rowGroupId) {
        setSelectedRows({
          ...selectedRows,
          [rowGroupId]: isSelected ? portfolioItem.assets.map((assetItem: Asset): number => assetItem.id) : [],
        });
      }
    });

    if (!isSelected) {
      setWithAllRowsSelected(false);
    }
  }, [assetListData, selectedRows]);

  const onSelectRow: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, rowGroupId: number, rowId: number,
  ): void => {
    setSelectedRows({
      ...selectedRows,
      [rowGroupId]: StructureService.selectRow(selectedRows, rowId, rowGroupId),
    });
    setWithAllRowsSelected(false);

    event.stopPropagation();
  }, [selectedRows]);

  const checkIsRowSelected: Function = useCallback((rowGroupId: number, rowId: number): boolean => (
    selectedRows[rowGroupId] ? selectedRows[rowGroupId].includes(rowId) : false
  ), [selectedRows]);

  const checkIsRowsSelected: Function = useCallback((): boolean => {
    let result: boolean = false;
    Object.keys(selectedRows).forEach((portfolioItemId: string): void => {
      if (selectedRows[portfolioItemId].length > 0) {
        result = true;
      }
    });

    return result;
  }, [selectedRows]);

  const isExportDisabled: boolean = useMemo((): boolean => (
    !selectedRows || !checkIsRowsSelected()
  ), [selectedRows]);

  const onSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    const assetSelectedRows: Record<string, number[]> = assetListData.reduce((
      resultAssetList: Record<string, number[]>, portfolioItem: Portfolio,
    ): Record<string, number[]> => ({
      ...resultAssetList,
      [portfolioItem.id]: isSelected ? portfolioItem.assets.map((assetItem: Asset): number => assetItem.id) : [],
    }), {});

    setSelectedRows(assetSelectedRows);
    setWithAllRowsSelected(!withAllRowsSelected);
  }, [assetListData, assetListData, withAllRowsSelected]);

  const expansionGroupChange: Function = useCallback((groupIndex: number): void => {
    if (expandedGroups) {
      if (expandedGroups.includes(groupIndex)) {
        delete expandedGroups[expandedGroups.indexOf(groupIndex)];

        setExpandedGroups([...expandedGroups.filter((expandedGroup: number): boolean => !!expandedGroup)]);
      } else {
        setExpandedGroups([...expandedGroups, groupIndex]);
      }
    }
  }, [expandedGroups]);

  const onLoadBatchData: Function = useCallback((allRows?: boolean, onSuccess?: Function): void => {
    dispatch(getAssetListData(
      getOptions(portfolioIdsFilter, portfolioGroupIdsFilter, page + PAGE_LIST_DEFAULT, allRows || false),
      (): void => {
        if (onSuccess) {
          onSuccess();
        }
      },
    ));
    setPage(page + PAGE_LIST_DEFAULT);
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, page]);

  const onExpansionGroupChange: Function = useCallback((
    event: React.SyntheticEvent, groupIndex: number, withLoading?: boolean,
  ): void => {
    if (withLoading) {
      onLoadBatchData(true, (): void => {
        expansionGroupChange(groupIndex);
      });
    } else {
      expansionGroupChange(groupIndex);
    }

    event.stopPropagation();
  }, [expandedGroups, portfolioIdsFilter, portfolioGroupIdsFilter, page]);

  const onOpenNewSellerForm: Function = useCallback((): void => {
    setIsNewSellerVisible(true);
  }, []);

  const onCloseNewSellerForm: Function = useCallback((): void => {
    setIsNewSellerVisible(false);
  }, []);

  const onRowClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, portfolioId: number, assetId: number, isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode) {
      if (isExportMode || isSelectMode) {
        onSelectRow(event, portfolioId, assetId);
      } else if (isEditMode && isListView) {
        const queryParams: LocationQueryParams = {
          [QUERY_PARAM_EDIT_MODE]: isEditMode,
        };

        navigate({
          pathname: `${ROUTE_ASSET}/${assetId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      } else if (!isEditMode) {
        navigate({
          pathname: `${ROUTE_ASSET}/${assetId}`,
        });
      }
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onTableColumnSettingsChange: Function = useCallback((
    updatedTableColumnSettings: TableField[],
  ): void => {
    if (updatedTableColumnSettings && updatedTableColumnSettings.length) {
      setTableColumnSettings(updatedTableColumnSettings);

      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_ASSET_LIST,
        tableColumnSettings: updatedTableColumnSettings,
      }));

      setExtendedTableFields(
        StructureService.extendOptions(
          tableAssetListFields.filter((tableField: TableField): boolean => !tableField.isEditModeOnly),
          updatedTableColumnSettings,
        ),
      );
    }
  }, [tableAssetListFields]);

  const onLongClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, portfolioId: number, assetId: number,
  ): void => {
    if (!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode) {
      dispatch(toggleSelectMode(true));
      onSelectRow(event, portfolioId, assetId);
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onDropColumn: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (tableColumnSettings) {
      onTableColumnSettingsChange(StructureService.switchListItems(tableColumnSettings, dragIndex, hoverIndex));
    }
  }, [extendedTableFields, tableColumnSettings, ...tableColumnDropDeps]);

  const onDropGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    const newPortfolioIds: number[] = [...portfolioIds];
    const activePortfolioId: number = newPortfolioIds[dragIndex];
    StructureService.deleteListItemByIndex(newPortfolioIds, dragIndex);
    StructureService.insertListItemByIndex(newPortfolioIds, hoverIndex, activePortfolioId);

    dispatch(savePortfolioOrderSettings({
      portfolioIds: newPortfolioIds.filter((portfolioId: number): boolean => !!portfolioId),
    }));
  }, [portfolioIds]);

  const onHoverGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    setActivePortfolioIndex(dragIndex);
    setHoverPortfolioIndex(hoverIndex);
  }, []);

  const onReleaseGroupItem: Function = useCallback((): void => {
    setActivePortfolioIndex(null);
    setHoverPortfolioIndex(null);
  }, []);

  const onTagsChange: Function = useCallback((assetId: number, portfolioId: number, tags: Tag[]): void => {
    const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
    const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);

    changeForm(`assetListData[${formKeyPortfolio}].assets[${formKeyAsset}].tags`, tags);
  }, []);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        isSmallScreen ? containerRef.current.offsetTop : containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (assetsTotal > commonAssetsTotal && !isLoading && !isCustomizeMode) {
            onLoadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [
    containerRef, itemListRef, isSmallScreen, assetsTotal, portfolioIdsFilter, portfolioGroupIdsFilter, page,
    commonAssetsTotal, isLoading, isCustomizeMode,
  ]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [
    assetListData, selectedRowsDefault, isPortfolioModalOpen, isPortfolioGroupModalOpen, isNewAsset, inlineSaveAssetId,
    inlineSavePortfolioId, assetActiveIds, portfolioActiveIds, isEditMode, initialFormValues, assetPhotoFiles,
    assetFieldNewItems,
  ]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getAssetListData(
      getOptions(
        queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
        queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
          ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
      ),
    ));
  }, [queryFilterPortfolioIds, queryFilterPortfolioGroupIds, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (!StructureService.checkIfArraysEqual(portfolioIdsFilter, queryFilterPortfolioIds || undefined)
      || !StructureService.checkIfArraysEqual(portfolioGroupIdsFilter, queryFilterPortfolioGroupIds || undefined)) {
      updateQueryParams();
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));

      setTableColumnSettings(tableColumnSettingsCommon && tableColumnSettingsCommon.value
        && tableColumnSettingsCommon.value[TABLE_NAME_ASSET_LIST] as TableField[]);
    }
  }, [currentUser]);

  useEffect((): void => {
    if (tableColumnSettings && tableColumnSettings.length && !extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(tableAssetListFields, tableColumnSettings, isEditMode));
    }
  }, [tableColumnSettings]);

  useEffect((): void => {
    if (extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(
        tableAssetListFields, tableColumnSettings, isEditMode,
      ));
    }
  }, [isEditMode, isExportMode, isSelectMode, isCustomizeMode, assetActiveIds, isFormSaving]);

  useEffect((): void => {
    if (portfolioIdsFilter && portfolioIdsFilter.length) {
      const newPortfolioIdsFilter: number[] = [...portfolioIdsFilter];

      if (portfolioList && portfolioList.length) {
        portfolioIdsFilter.forEach((portfolioId: number, portfolioIdIndex: number): void => {
          if (!portfolioList.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId)) {
            StructureService.deleteListItemByIndex(newPortfolioIdsFilter, portfolioIdIndex);
          }
        });

        if (newPortfolioIdsFilter.length < portfolioIdsFilter.length) {
          dispatch(setPortfolioIdsFilter(newPortfolioIdsFilter));
        }
      }
    }
  }, [portfolioList]);

  useEffect((): void => {
    openExpansionGroups();
  }, [isDataLoaded]);

  useEffect((): void => {
    if (!isDataLoaded) {
      setPage(PAGE_LIST_DEFAULT);

      if (queryFilterPortfolioIds && queryFilterPortfolioIds.length) {
        dispatch(setPortfolioIdsFilter(queryFilterPortfolioIds));
      }

      if (queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length) {
        dispatch(setPortfolioGroupIdsFilter(queryFilterPortfolioGroupIds));

        if (!queryFilterPortfolioIds || !queryFilterPortfolioIds.length) {
          dispatch(setPortfolioIdsFilter(undefined));
        }
      }

      dispatch(getAssetListData(
        getOptions(
          queryFilterPortfolioIds && queryFilterPortfolioIds.length ? queryFilterPortfolioIds : portfolioIdsFilter,
          queryFilterPortfolioGroupIds && queryFilterPortfolioGroupIds.length
            ? queryFilterPortfolioGroupIds : portfolioGroupIdsFilter,
        ),
      ));
    }
  }, [isDataLoaded]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getAssetListData(getOptions(portfolioIdsFilter, portfolioGroupIdsFilter)));
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (assetListData) {
      const initialSortParams: Record<string, SortParams> = sortParams || {};
      assetListData.forEach((portfolioItem: Portfolio): void => {
        if (!initialSortParams[portfolioItem.id]) {
          initialSortParams[portfolioItem.id] = DEFAULT_SORT;
        }
      });
      setSortParams(initialSortParams);

      const initialRowsSelectedDefault: Record<string, number[]> = {};
      const initialRowsSelected: Record<string, number[]> = {};
      assetListData.forEach((portfolioItem: Portfolio): void => {
        initialRowsSelectedDefault[portfolioItem.id] = [];
        initialRowsSelected[portfolioItem.id] = (selectedRows && selectedRows[portfolioItem.id]) || [];
      });
      setSelectedRowsDefault(initialRowsSelectedDefault);
      setSelectedRows(initialRowsSelected);

      const initialAssetAddressList: Record<string, JsonObject> = {};
      assetListData.forEach((portfolioItem: Portfolio): void => {
        portfolioItem.assets.forEach((assetItem: Asset): void => {
          initialAssetAddressList[assetItem.id] = assetItem.address;
        });
      });
      setAssetAddressList(initialAssetAddressList);
    }
  }, [assetListData]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastPortfolioIdsValue) {
        dispatch(setPortfolioIdsFilter(lastPortfolioIdsValue));

        setLastPortfolioIdsValue(undefined);
      }

      if (lastPortfolioGroupIdsValue) {
        dispatch(setPortfolioGroupIdsFilter(lastPortfolioGroupIdsValue));

        setLastPortfolioGroupIdsValue(undefined);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && inlineSaveAssetId !== null) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      setInlineSaveAssetId(null);
      setInlineSavePortfolioId(null);
      setInlineSaveIsConfirm(null);
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useScroll(onScroll, scrollDeps);

  useArrowNavigation(inputRefs);

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!isDataLoaded || !tableColumnSettings) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={assetListViewPermissions}>
      <Form id={FORM_ID_ASSET_LIST} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader
            title={pageTitle}
            leftBlockComponent={isPortfolioFilterViewAllowed && !!portfolioGroupList && !isSmallScreen && (
              <PortfolioFilter
                isDisabled={isFormChanged}
                portfolioGroupList={portfolioGroupList}
                onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
              />
            )}
          />
          <CheckAccess permissions={assetStatisticsViewPermissions}>
            <StatisticsBlock statisticItemList={statisticsData} />
          </CheckAccess>
          {
            isSmallScreen && (
              <CheckAccess permissions={assetPortfolioFilterPermissions}>
                <PortfolioFilter
                  isInline
                  isDisabled={isFormChanged}
                  portfolioGroupList={portfolioGroupListFiltered}
                  onPortfolioFilterValuesChange={onPortfolioFilterValuesChange}
                  onBeforePortfolioFilterValuesChange={onBeforePortfolioFilterValuesChange}
                />
              </CheckAccess>
            )
          }
          <Controls
            withGroups
            isTableExport
            withSelectAll
            withSelectMode
            withColumnSettings
            isSaveDisabled={isSaveDisabled}
            isExportDisabled={isExportDisabled}
            isCustomizable={isCustomizable}
            isCustomizeMode={isCustomizeMode}
            isGroupExpanded={isGroupExpanded}
            withAllRowsSelected={withAllRowsSelected}
            rowsTotal={assetsTotal}
            rowsSelectedTotal={assetsSelectedTotal}
            selectedItemIds={selectedRows}
            portfolioIds={portfolioIdsFilter}
            portfolioGroupIds={portfolioGroupIdsFilter}
            exportFormatList={FORMAT_LIST_EXPORT_ASSET}
            importFormatList={FORMAT_LIST_IMPORT_ASSET}
            importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_ASSET_LIST}
            createButtonTitle={createButtonTitle}
            createGroupButtonTitle={createGroupButtonTitle}
            createAdditionalItemButtonTitle={createAdditionalItemButtonTitle}
            createPermission={ASSET_CREATE}
            createGroupPermission={PORTFOLIO_CREATE}
            createAdditionalItemPermission={PORTFOLIO_GROUP_CREATE}
            editPermissions={assetEditPermissions}
            importPermission={ASSET_LIST_IMPORT}
            exportPermission={ASSET_LIST_EXPORT}
            deletePermission={ASSET_DELETE}
            tableFields={tableAssetListFields}
            tableFieldGroups={tableAssetListFieldGroups}
            tableColumnSettings={tableColumnSettings}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            onExportClick={openExportModal}
            onDeleteClick={openAssetDeleteConfirmModal}
            onCreateButtonClick={onCreateAssetButtonClick}
            onCreateGroupButtonClick={onCreatePortfolioButtonClick}
            onCreateAdditionalItemButtonClick={onCreatePortfolioGroupButtonClick}
            onSelectAllRows={onSelectAllRows}
            onImportSuccess={onImportSuccess}
            onExpandAll={openExpansionGroups}
            onCollapseAll={closeExpansionGroups}
            onTableColumnsChange={onTableColumnSettingsChange}
          />
          {
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode && portfolioAssetLocationItemList
            && !!portfolioAssetLocationItemList.length && (
              <ExpansionGroupWidget headerName="Asset Locations" permission={ASSET_LIST_ASSET_LOCATIONS_VIEW}>
                <LocationMap
                  defaultColor={MARKER_COLOR_RED}
                  colorIdList={portfolioMarkerColorIdList}
                  locationItemList={portfolioAssetLocationItemList}
                />
              </ExpansionGroupWidget>
            )
          }
          {
            assetListData && !!assetListData.length && extendedTableFields && !!extendedTableFields.length ? (
              <Card>
                <ContentListContainerOffset
                  withStatisticsBar
                  isDefault={isSmallScreen}
                  withMap={withMap}
                  withButtons={withButtons}
                  ref={containerRef}
                  onScroll={!isSmallScreen ? onScroll : undefined}
                >
                  <div ref={itemListRef}>
                    {
                      SortService.sort(assetListData, GROUP_SORT_STATE).map((
                        portfolio: Portfolio, portfolioIndex: number,
                      ): ReactElement | null => {
                        const portfolioAssetsTotal: number = portfolio ? portfolio.assets.length : 0;
                        const portfolioAssetsSelectedTotal: number = selectedRows && selectedRows[portfolio.id]
                          ? selectedRows[portfolio.id].length : 0;
                        const isPortfolioSelected: boolean = withAllRowsSelected
                          || portfolioAssetsSelectedTotal === portfolioAssetsTotal;
                        const isDealStatusIndeterminate: boolean = !withAllRowsSelected
                          && portfolioAssetsSelectedTotal > 0
                          && portfolioAssetsSelectedTotal < portfolioAssetsTotal;
                        const visibleTableFieldsTotal: number = extendedTableFields
                          .filter((tableField: TableField): boolean => (
                            !!tableField.isVisible && (isEditMode ? true : !tableField.isEditModeOnly)
                          )).length;
                        const withGroupItems: boolean = portfolio.assets && !!portfolio.assets.length;
                        const isGroupLoading: boolean = !!portfolio.assetsTotal
                          && portfolio.assetsTotal > portfolioAssetsTotal;
                        const isActiveGroup: boolean = checkIsPortfolioActive(portfolio.id);
                        const isGroupCollapsed: boolean = expandedGroups
                          && !expandedGroups.includes(portfolioIndex + 1);
                        const isCurrentTableLoading: boolean = isTableLoading;
                        const isGroupRendered: boolean = ((!!portfolio.assetsTotal && !!portfolioAssetsTotal)
                          || !portfolio.assetsTotal) && !isCurrentTableLoading;
                        const isVisible: boolean = (!isExportMode && !isSelectMode && !isEditMode)
                          || ((isExportMode || isSelectMode || isEditMode) && (withGroupItems || isGroupLoading));

                        isTableLoading = isTableLoading || isGroupLoading;

                        return (isGroupRendered || isGroupCollapsed) ? (
                          <DraggableItem
                            withHighlight
                            isDisabled={!isGroupOrderAllowed}
                            isHovered={hoverPortfolioIndex === portfolioIndex}
                            isHoverInverted={hoverPortfolioIndex && activePortfolioIndex
                              && hoverPortfolioIndex > activePortfolioIndex}
                            key={portfolio.id}
                            itemId={portfolio.id}
                            index={portfolioIndex}
                            onDropItem={onDropGroupItem}
                            onHoverItem={onHoverGroupItem}
                            onReleaseItem={onReleaseGroupItem}
                          >
                            <ExpansionGroup
                              isTableGroup
                              isExpanded={(isSmallScreen && !isGroupCollapsed) || !isSmallScreen}
                              withNoHeader={!isSmallScreen}
                              onChange={(event: React.ChangeEvent): void => {
                                onExpansionGroupChange(event, portfolioIndex + 1, !isGroupRendered);
                              }}
                              headerComponent={isSmallScreen && (
                                <Fragment>
                                  {
                                    !isEditMode || !isActiveGroup ? (
                                      <FlexContainer isSpread>
                                        <FlexContainer isSpread isStart>
                                          {
                                            isMultiplePortfolioGroupView && portfolio.group ? (
                                              <TextBlock>
                                                <TextBold>
                                                  {portfolio.group.name}
                                                </TextBold>
                                                :
                                                {' '}
                                                {portfolio.name}
                                              </TextBlock>
                                            ) : portfolio.name
                                          }
                                        </FlexContainer>
                                        <ActionButtonContainer>
                                          {
                                            !isActiveGroup && (
                                              <CheckAccess permissions={portfolioEditPermissions}>
                                                <ActionButton
                                                  isLight
                                                  isHidden={!!isFormSaving}
                                                  withNoPadding
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onInlineGroupEditModeButtonClick(event, portfolio.id);
                                                  }}
                                                >
                                                  <EditIcon />
                                                </ActionButton>
                                              </CheckAccess>
                                            )
                                          }
                                          {
                                            !isSmallScreen && (
                                              <CheckAccess permissions={portfolioDeletePermissions}>
                                                <ActionButton
                                                  isLight
                                                  isActive
                                                  withPopup
                                                  withNoPadding
                                                  onClick={openGroupActionMenu}
                                                  {...{
                                                    [DATA_PORTFOLIO_ID]: portfolio.id,
                                                  }}
                                                >
                                                  <MoreIcon />
                                                </ActionButton>
                                              </CheckAccess>
                                            )
                                          }
                                        </ActionButtonContainer>
                                      </FlexContainer>
                                    ) : (
                                      <FlexContainer isSpread>
                                        <CheckAccess permissions={portfolioEditPermissions}>
                                          <div
                                            ref={(ref: HTMLDivElement): void => {
                                              if (portfolioFieldRefs.current) {
                                                portfolioFieldRefs.current[portfolio.id] = ref;
                                              }
                                            }}
                                          >
                                            <FormFieldText
                                              isLight
                                              withUnderline
                                              name={`${formPrefix}[${portfolio.id}].name`}
                                              placeholder="Portfolio Name"
                                              onClick={onGroupFieldClick}
                                            />
                                          </div>
                                        </CheckAccess>
                                      </FlexContainer>
                                    )
                                  }
                                </Fragment>
                              )}
                            >
                              {
                                !isSmallScreen && (
                                  <TableResize
                                    isActive={isCustomizeMode && !isGroupCollapsed}
                                    isDisabled={isEditMode}
                                    groupId={portfolio.id}
                                    tableRef={tableRefs}
                                    headerCellRefs={headerCellRefs}
                                    tableColumnSettings={tableColumnSettings}
                                    onTableColumnsChange={onTableColumnSettingsChange}
                                  >
                                    <HiddenBlock isActive={!isVisible}>
                                      <Table
                                        withStickyHeader
                                        isFullWidth={isEditMode}
                                        ref={(ref: HTMLTableElement): void => {
                                          if (tableRefs.current) {
                                            tableRefs.current[portfolio.id] = ref;
                                          }
                                        }}
                                      >
                                        <TableHead isSubHeader>
                                          {
                                            !isSmallScreen && (
                                              !isEditMode || !isActiveGroup ? (
                                                <TableRowGroupName
                                                  isButton
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onExpansionGroupChange(
                                                      event,
                                                      portfolioIndex + 1,
                                                      !isGroupRendered,
                                                    );
                                                  }}
                                                >
                                                  {
                                                    (isExportMode || isSelectMode) && (
                                                      <TableCell
                                                        isSticky
                                                        isStickyLeft
                                                        isStickyGroupLeft
                                                      />
                                                    )
                                                  }
                                                  <TableCell
                                                    isSticky={!isCustomizeMode}
                                                    isStickyLeft={!isCustomizeMode
                                                      && !isExportMode && !isSelectMode}
                                                    isStickyLeftSelect={!isCustomizeMode
                                                      && (isExportMode || isSelectMode)}
                                                    isStickyGroupLeft={!isCustomizeMode
                                                      && !isExportMode && !isSelectMode}
                                                    isStickyGroupLeftSelect={!isCustomizeMode
                                                      && (isExportMode || isSelectMode)}
                                                    colSpan={isCustomizeMode && !isGroupOrderAllowed ? 1 : 3}
                                                  >
                                                    {
                                                      isGroupOrderAllowed && (
                                                        <DragHandle />
                                                      )
                                                    }
                                                    {
                                                      isMultiplePortfolioGroupView && portfolio.group ? (
                                                        <TextBlock>
                                                          <TextBold>
                                                            {portfolio.group.name}
                                                          </TextBold>
                                                          :
                                                          {' '}
                                                          {portfolio.name}
                                                        </TextBlock>
                                                      ) : portfolio.name
                                                    }
                                                  </TableCell>
                                                  <TableCell
                                                    colSpan={(
                                                      visibleTableFieldsTotal - (isCustomizeMode
                                                      && !isGroupOrderAllowed ? 1 : ((isEditMode && 2) || 3))
                                                    )}
                                                  />
                                                  <TableCell
                                                    isActions
                                                    isTextRight
                                                    isSticky={!isCustomizeMode}
                                                    isStickyRight={!isCustomizeMode}
                                                  >
                                                    <ActionButtonContainer>
                                                      {
                                                        isEditMode && !isActiveGroup && (
                                                          <CheckAccess permissions={portfolioEditPermissions}>
                                                            <ActionButton
                                                              isLight
                                                              withNoPadding
                                                              isHidden={!!isFormSaving}
                                                              onClick={(
                                                                event: React.MouseEvent<Element, MouseEvent>,
                                                              ): void => {
                                                                onInlineGroupEditModeButtonClick(
                                                                  event, portfolio.id,
                                                                );
                                                              }}
                                                            >
                                                              <EditIcon />
                                                            </ActionButton>
                                                          </CheckAccess>
                                                        )
                                                      }
                                                      {
                                                        !isExportMode && !isSelectMode && !isCustomizeMode
                                                        && !isEditMode && !isSmallScreen && (
                                                          <CheckAccess permissions={portfolioDeletePermissions}>
                                                            <ActionButton
                                                              isLight
                                                              isActive
                                                              withPopup
                                                              withNoPadding
                                                              onClick={openGroupActionMenu}
                                                              {...{
                                                                [DATA_PORTFOLIO_ID]: portfolio.id,
                                                              }}
                                                            >
                                                              <MoreIcon />
                                                            </ActionButton>
                                                          </CheckAccess>
                                                        )
                                                      }
                                                      {
                                                        !isSmallScreen && (
                                                          <ActionButton
                                                            isLight
                                                            withNoPadding
                                                            isDisabled={!isGroupRendered}
                                                            onClick={(
                                                              event: React.MouseEvent<Element, MouseEvent>,
                                                            ): void => {
                                                              onExpansionGroupChange(
                                                                event,
                                                                portfolioIndex + 1,
                                                                !isGroupRendered,
                                                              );
                                                            }}
                                                          >
                                                            {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                          </ActionButton>
                                                        )
                                                      }
                                                    </ActionButtonContainer>
                                                  </TableCell>
                                                </TableRowGroupName>
                                              ) : (
                                                <TableRowGroupName
                                                  isButton
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onExpansionGroupChange(
                                                      event,
                                                      portfolioIndex + 1,
                                                      !isGroupRendered,
                                                    );
                                                  }}
                                                >
                                                  <CheckAccess permissions={portfolioEditPermissions}>
                                                    <TableCell isSticky isStickyLeft isStickyGroupLeft colSpan={2}>
                                                      <FlexContainer isSpread>
                                                        <CheckAccess permissions={portfolioEditPermissions}>
                                                          <div
                                                            ref={(ref: HTMLDivElement): void => {
                                                              if (portfolioFieldRefs.current) {
                                                                portfolioFieldRefs.current[portfolio.id] = ref;
                                                              }
                                                            }}
                                                          >
                                                            <FormFieldText
                                                              isLight
                                                              withUnderline
                                                              name={`${formPrefix}[${portfolio.id}].name`}
                                                              placeholder="Portfolio Name"
                                                              onClick={onGroupFieldClick}
                                                            />
                                                          </div>
                                                        </CheckAccess>
                                                      </FlexContainer>
                                                    </TableCell>
                                                    <TableCell colSpan={visibleTableFieldsTotal - 1} />
                                                    <TableCell
                                                      isActions
                                                      isTextRight
                                                      isSticky={!isCustomizeMode}
                                                      isStickyRight={!isCustomizeMode}
                                                    >
                                                      <ActionButtonContainer>
                                                        {
                                                          !isSmallScreen && (
                                                            <ActionButton
                                                              isLight
                                                              withNoPadding
                                                              isDisabled={!isGroupRendered}
                                                              onClick={(
                                                                event: React.MouseEvent<Element, MouseEvent>,
                                                              ): void => {
                                                                onExpansionGroupChange(
                                                                  event,
                                                                  portfolioIndex + 1,
                                                                  !isGroupRendered,
                                                                );
                                                              }}
                                                            >
                                                              {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                            </ActionButton>
                                                          )
                                                        }
                                                      </ActionButtonContainer>
                                                    </TableCell>
                                                  </CheckAccess>
                                                </TableRowGroupName>
                                              )
                                            )
                                          }
                                          {
                                            portfolio.assets && !!portfolio.assets.length && (
                                              <TableRowGroupHeader
                                                isSticky
                                                isEdit={isEditMode && isActiveGroup}
                                                isHidden={!isSmallScreen && isGroupCollapsed}
                                              >
                                                {
                                                  (isExportMode || isSelectMode) && (
                                                    <TableCell
                                                      isSticky={!isCustomizeMode}
                                                      isStickyLeft={!isCustomizeMode}
                                                    >
                                                      <Checkbox
                                                        isHeader
                                                        isChecked={isPortfolioSelected}
                                                        isIndeterminate={isDealStatusIndeterminate}
                                                        id={`${ID_PREFIX_CHECKBOX_EXPORT_GROUP}-${portfolio.id}`}
                                                        onChange={(event: React.ChangeEvent): void => {
                                                          onSelectAllGroupRows(event, portfolio.id);
                                                        }}
                                                      />
                                                    </TableCell>
                                                  )
                                                }
                                                {
                                                  extendedTableFields.map((
                                                    tableField: TableField, tableFieldIndex: number,
                                                  ): ReactElement => (
                                                    <VisibleCell
                                                      isEditMode={isEditMode}
                                                      key={tableField.id}
                                                      tableField={tableField}
                                                    >
                                                      <FilteredCell
                                                        columnName={tableField.name}
                                                        tableName={TABLE_NAME_ASSET_LIST}
                                                        currentUser={currentUser}
                                                      >
                                                        <TableCell
                                                          isActions={tableField.name === HEADER_NAME_ACTIONS}
                                                          isNumber={tableField.isNumber && !isEditMode}
                                                          isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                                          isSticky={!isCustomizeMode && tableField.isSticky}
                                                          isStickyLeft={!isCustomizeMode && !isExportMode
                                                            && !isSelectMode && tableField.isSticky
                                                            && !tableField.isStickyRight}
                                                          isStickyLeftSelect={!isCustomizeMode
                                                            && (isExportMode || isSelectMode) && tableField.isSticky
                                                            && !tableField.isStickyRight}
                                                          isStickyRight={!isCustomizeMode && tableField.isSticky
                                                            && tableField.isStickyRight}
                                                          isStickyRightHeader={!isCustomizeMode
                                                            && !isExportMode && !isSelectMode
                                                            && tableField.isSticky && tableField.isStickyRight}
                                                          ref={(ref: HTMLElement): void => {
                                                            if (headerCellRefs.current) {
                                                              if (!headerCellRefs.current[portfolio.id]) {
                                                                headerCellRefs.current[portfolio.id] = {};
                                                              }

                                                              headerCellRefs.current[portfolio.id][tableFieldIndex] = (
                                                                ref
                                                              );
                                                            }
                                                          }}
                                                          sortDirection={(
                                                            sortParams && sortParams[portfolio.id]
                                                            && tableField.isSortable
                                                            && sortParams[portfolio.id].field === tableField.name
                                                              ? sortParams[portfolio.id].type : false
                                                          )}
                                                        >
                                                          {
                                                            tableField.isSortable && !isEditMode ? (
                                                              <DraggableItem
                                                                isDisabled={!isCustomizeMode}
                                                                itemId={tableField.id}
                                                                index={tableFieldIndex}
                                                                dropDeps={tableColumnDropDeps}
                                                                onDropItem={onDropColumn}
                                                              >
                                                                <TableSortLabel
                                                                  withNoLabel={tableField.isHeaderNameHidden}
                                                                  isActive={sortParams && sortParams[portfolio.id]
                                                                    ? sortParams[portfolio.id].field
                                                                    === tableField.name : false}
                                                                  sortType={(
                                                                    !!sortParams && !!sortParams[portfolio.id]
                                                                      ? sortParams[portfolio.id].type : SORT_TYPE_ASC
                                                                  )}
                                                                  onClick={(): void => {
                                                                    sortTableRows(
                                                                      portfolio.id, tableField.name,
                                                                      tableField.isNumber,
                                                                    );
                                                                  }}
                                                                >
                                                                  {tableField.title}
                                                                </TableSortLabel>
                                                              </DraggableItem>
                                                            ) : (
                                                              !tableField.isHeaderNameHidden && (
                                                                <DraggableItem
                                                                  isDisabled={!isCustomizeMode}
                                                                  itemId={tableField.id}
                                                                  index={tableFieldIndex}
                                                                  dropDeps={tableColumnDropDeps}
                                                                  onDropItem={onDropColumn}
                                                                >
                                                                  {tableField.title}
                                                                  {tableField.isRequired && <RequiredMark />}
                                                                </DraggableItem>
                                                              )
                                                            )
                                                          }
                                                        </TableCell>
                                                      </FilteredCell>
                                                    </VisibleCell>
                                                  ))
                                                }
                                              </TableRowGroupHeader>
                                            )
                                          }
                                        </TableHead>
                                        <TableBody
                                          isPlain
                                          withZebraRows
                                          isHidden={!isSmallScreen && isGroupCollapsed}
                                          withBorderedCells={!isCustomizeMode}
                                          withFixedCellsSizeExtended={isEditMode}
                                        >
                                          {
                                            portfolio.assets && !!portfolio.assets.length
                                            && sortParams && sortParams[portfolio.id]
                                            && SortService.sort(portfolio.assets, sortParams[portfolio.id])
                                              .map((asset: Asset): ReactElement => {
                                                const isSelected: boolean = selectedRows
                                                  ? withAllRowsSelected || checkIsRowSelected(portfolio.id, asset.id)
                                                  : false;
                                                const isActive: boolean = checkIsAssetActive(asset.id);
                                                const isInlineSaving: boolean = inlineSaveAssetId !== null
                                                && inlineSavePortfolioId !== null ? asset.id === inlineSaveAssetId
                                                  && portfolio && portfolio.id === inlineSavePortfolioId
                                                  : isFormSaving;
                                                const commonAssetIndex: number = commonRowIndex;
                                                commonRowIndex++;

                                                return (
                                                  <AssetRow
                                                    isActive={isActive}
                                                    isSaving={isFormSaving}
                                                    isSaveDisabled={isSaveDisabled}
                                                    isInlineSaving={isInlineSaving}
                                                    isSelected={isSelected}
                                                    isEditMode={isEditMode}
                                                    isExportMode={isExportMode}
                                                    isSelectMode={isSelectMode}
                                                    isCustomizeMode={isCustomizeMode}
                                                    isMultiplePortfolioGroupView={isMultiplePortfolioGroupView}
                                                    key={asset.id}
                                                    assetIndex={commonAssetIndex}
                                                    assetItem={asset}
                                                    portfolioItem={portfolio}
                                                    assetPhotoFile={assetPhotoFiles[asset.id]}
                                                    currentUser={currentUser}
                                                    inputRefs={inputRefs}
                                                    fileDropDeps={fileDropDeps}
                                                    tableName={TABLE_NAME_ASSET_LIST}
                                                    tableColumnSettings={tableColumnSettings}
                                                    tableAssetListFields={extendedTableFields}
                                                    assetStatusList={assetStatusList}
                                                    measureUnitList={measureUnitList}
                                                    pricingTypeList={pricingTypeList}
                                                    portfolioList={portfolioList}
                                                    assetTypeList={assetTypeList}
                                                    contactCompanyList={contactCompanyList}
                                                    assetOwnershipStructureTypeList={assetOwnershipStructureTypeList}
                                                    tagList={tagList}
                                                    onRowClick={onRowClick}
                                                    onLongClick={onLongClick}
                                                    onInlineSave={onInlineSave}
                                                    onInlineCancel={onInlineCancel}
                                                    onAssetPhotoChange={onAssetPhotoChange}
                                                    onAssetPhotoDelete={onAssetPhotoDelete}
                                                    onAddressSearchCompleted={onAddressSearchCompleted}
                                                    onAddressFieldKeyDown={onAddressFieldKeyDown}
                                                    onAssetTypesChange={onAssetTypesChange}
                                                    onAssetSellersChange={onAssetSellersChange}
                                                    onTagsChange={onTagsChange}
                                                    onContactCompanySearch={onContactCompanySearch}
                                                    onContactCompanySearchClose={onContactCompanySearchClose}
                                                    onTagSearch={onTagSearch}
                                                    onTagSearchClose={onTagSearchClose}
                                                  />
                                                );
                                              })
                                          }
                                          {
                                            (!portfolio.assets || !portfolio.assets.length) && (
                                              <TableRowNoContent>
                                                <TableCell
                                                  isSticky={!isCustomizeMode}
                                                  isStickyLeftSelect={!isCustomizeMode
                                                    && (isExportMode || isSelectMode)}
                                                >
                                                  No assets in this portfolio
                                                </TableCell>
                                                <TableCell colSpan={visibleTableFieldsTotal} />
                                                {
                                                  isEditMode && (
                                                    <TableCell isActions isTextRight />
                                                  )
                                                }
                                              </TableRowNoContent>
                                            )
                                          }
                                          {
                                            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                                            && portfolio.assets && !!portfolio.assets.length
                                            && portfolio.portfolioSummaryData && (
                                              <TableRowSummary withNoHighlight>
                                                {
                                                  extendedTableFields.map((tableField: TableField): ReactElement => (
                                                    <VisibleCell
                                                      isEditMode={isEditMode}
                                                      key={tableField.id}
                                                      tableField={tableField}
                                                    >
                                                      {
                                                        !!tableField.summaryComponent && (
                                                          !tableField.isNotFiltered ? (
                                                            <FilteredCell
                                                              tableName={TABLE_NAME_ASSET_LIST}
                                                              columnName={tableField.name}
                                                              currentUser={currentUser}
                                                            >
                                                              {
                                                                tableField.summaryComponent(
                                                                  portfolio.portfolioSummaryData,
                                                                )
                                                              }
                                                            </FilteredCell>
                                                          ) : (
                                                            tableField.summaryComponent(portfolio.portfolioSummaryData)
                                                          )
                                                        )
                                                      }
                                                    </VisibleCell>
                                                  ))
                                                }
                                              </TableRowSummary>
                                            )
                                          }
                                        </TableBody>
                                      </Table>
                                    </HiddenBlock>
                                  </TableResize>
                                )
                              }
                              {
                                isSmallScreen && (
                                  <Fragment>
                                    {
                                      !!assetListData.length && (
                                        <List>
                                          {
                                            portfolio.assets && !!portfolio.assets.length
                                            && portfolio.assets.map((asset: Asset): ReactElement => {
                                              const isSelected: boolean = selectedRows
                                                ? withAllRowsSelected || checkIsRowSelected(portfolio.id, asset.id)
                                                : false;

                                              return (
                                                <LongClickListItem
                                                  isButton={!isSelectMode && !isExportMode}
                                                  isActive={isSelected}
                                                  key={asset.id}
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onRowClick(event, portfolio.id, asset.id, true);
                                                  }}
                                                  onLongClick={(
                                                    event: React.MouseEvent<Element, MouseEvent>,
                                                  ): void => {
                                                    onLongClick(event, portfolio.id, asset.id);
                                                  }}
                                                >
                                                  {
                                                    (isExportMode || isSelectMode) && (
                                                      <ListItemCheckbox isChecked={isSelected} itemId={asset.id} />
                                                    )
                                                  }
                                                  <ListItemImage
                                                    text={asset.name}
                                                    link={asset.mainPhotoThumbnail || asset.mainPhoto
                                                      || AssetDefaultImage}
                                                  />
                                                  <ListItemText isBlock>
                                                    {asset.name}
                                                  </ListItemText>
                                                  <ListItemText
                                                    isBlock
                                                    isBlockRight
                                                    subText={!isEditMode ? asset.amount : (
                                                      <FlexContainer isEnd>
                                                        <ActionButton
                                                          withSmallPadding
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onRowClick(event, portfolio.id, asset.id, true);
                                                          }}
                                                        >
                                                          <ArrowLinkIcon />
                                                        </ActionButton>
                                                      </FlexContainer>
                                                    )}
                                                  />
                                                </LongClickListItem>
                                              );
                                            })
                                          }
                                          {
                                            (!portfolio.assets || !portfolio.assets.length) && (
                                              <NoContentBlock>
                                                No assets in this portfolio
                                              </NoContentBlock>
                                            )
                                          }
                                        </List>
                                      )
                                    }
                                  </Fragment>
                                )
                              }
                            </ExpansionGroup>
                          </DraggableItem>
                        ) : null;
                      })
                    }
                  </div>
                  {
                    isLoading && isDataLoaded && (
                      <Loader isInline />
                    )
                  }
                  {
                    !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                    && !!assetListData.length && assetListSummaryData && (
                      <Fragment>
                        {
                          !isSmallScreen && (
                            <TableResize
                              isActive={false}
                              isDisabled={isEditMode}
                              groupId={GROUP_ID_SUMMARY}
                              tableRef={tableRefs}
                              headerCellRefs={headerCellRefs}
                              tableColumnSettings={tableColumnSettings}
                              onTableColumnsChange={onTableColumnSettingsChange}
                            >
                              <Table
                                ref={(ref: HTMLTableElement): void => {
                                  if (tableRefs.current) {
                                    tableRefs.current[GROUP_ID_SUMMARY] = ref;
                                  }
                                }}
                              >
                                <TableHead withNoPadding>
                                  <TableRow>
                                    {
                                      extendedTableFields.map((
                                        tableField: TableField, tableFieldIndex: number,
                                      ): ReactElement => (
                                        <VisibleCell
                                          isEditMode={isEditMode}
                                          key={tableField.id}
                                          tableField={tableField}
                                        >
                                          <FilteredCell
                                            columnName={tableField.name}
                                            tableName={TABLE_NAME_ASSET_LIST}
                                            currentUser={currentUser}
                                          >
                                            <TableCell
                                              isSticky={!isCustomizeMode && tableField.isSticky}
                                              isStickyRight={!isCustomizeMode && tableField.isSticky
                                                && tableField.isStickyRight}
                                              isStickyRightHeader={!isCustomizeMode
                                                && !isExportMode && !isSelectMode
                                                && tableField.isSticky && tableField.isStickyRight}
                                              ref={(ref: HTMLElement): void => {
                                                if (headerCellRefs.current) {
                                                  if (!headerCellRefs.current[GROUP_ID_SUMMARY]) {
                                                    headerCellRefs.current[GROUP_ID_SUMMARY] = {};
                                                  }

                                                  headerCellRefs.current[GROUP_ID_SUMMARY][tableFieldIndex] = ref;
                                                }
                                              }}
                                            />
                                          </FilteredCell>
                                        </VisibleCell>
                                      ))
                                    }
                                  </TableRow>
                                </TableHead>
                                <TableBody isPlain withZebraRows>
                                  <TableRowSummary withNoHighlight>
                                    {
                                      extendedTableFields.map((tableField: TableField): ReactElement => (
                                        <VisibleCell
                                          isEditMode={isEditMode}
                                          key={tableField.id}
                                          tableField={tableField}
                                        >
                                          {
                                            !!tableField.summaryComponent && (
                                              !tableField.isNotFiltered ? (
                                                <FilteredCell
                                                  tableName={TABLE_NAME_ASSET_LIST}
                                                  columnName={tableField.name}
                                                  currentUser={currentUser}
                                                >
                                                  {tableField.summaryComponent(assetListSummaryData)}
                                                </FilteredCell>
                                              ) : (
                                                tableField.summaryComponent(assetListSummaryData)
                                              )
                                            )
                                          }
                                        </VisibleCell>
                                      ))
                                    }
                                  </TableRowSummary>
                                </TableBody>
                              </Table>
                            </TableResize>
                          )
                        }
                        {
                          isSmallScreen && (
                            <ListItemSummary>
                              <ListItemText isBlock>
                                Total:
                              </ListItemText>
                              <ListItemText isBlock isBlockRight>
                                {assetListSummaryData.amount}
                              </ListItemText>
                            </ListItemSummary>
                          )
                        }
                      </Fragment>
                    )
                  }
                </ContentListContainerOffset>
              </Card>
            ) : (
              <NoContentBlock>
                No assets found by your request
              </NoContentBlock>
            )
          }
          <div>
            <ControlButtonBlock
              isExportMode={isExportMode}
              isSelectMode={isSelectMode}
              isEditMode={isEditMode}
              isSaveDisabled={isSaveDisabled}
              isExportDisabled={isExportDisabled}
              withOffset={isRightDrawerOpen}
              onCancelForm={onCancelForm}
              onExportClick={openExportModal}
              onSubmitForm={onSubmitForm}
            />
            {
              (!!formError || !!mainServerFormError) && !isFormSaving && (
                <FormError
                  withShowError={isEditMode}
                  withLeftOffset={isRightDrawerOpen}
                  error={formError || mainServerFormError}
                />
              )
            }
            {
              isFormSaving && isEditMode && (
                <Loader isInline isSubmit isFloating isHidden={isMobileScreen} />
              )
            }
          </div>
        </PullToRefresh>
        <ContextMenu
          menuAnchor={groupMenuAnchor}
          actionMenuItems={actionGroupMenuItems}
          dataAttributeEntityId={DATA_PORTFOLIO_ID}
          onClose={closeGroupActionMenu}
        />
        <ContextMenu
          menuAnchor={menuAnchor}
          actionMenuItems={actionMenuItems}
          dataAttributeEntityId={DATA_ASSET_ID}
          onClose={closeActionMenu}
        />
        <AssetModal
          isOpen={isAssetModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isNewSellerVisible={isNewSellerVisible}
          isMultiplePortfolioGroupView={isMultiplePortfolioGroupView}
          assetPhotoFile={assetPhotoFiles[FormService.FORM_KEY_NEW]}
          assetFieldNewItems={assetFieldNewItems[FormService.FORM_KEY_NEW]}
          portfolioList={portfolioList}
          assetStatusList={assetStatusList}
          assetTypeList={assetTypeList}
          measureUnitList={measureUnitList}
          pricingTypeList={pricingTypeList}
          assetOwnershipStructureTypeList={assetOwnershipStructureTypeList}
          contactCompanyList={contactCompanyList}
          tagList={tagList}
          saveFormDeps={saveFormDeps}
          onCreate={onAssetCreate}
          onCancel={onAssetModalCancel}
          onCloseModal={closeAssetModal}
          onSubmitCreateForm={onSubmitCreateForm}
          onAssetTypesChange={onAssetTypesChange}
          onAssetSellersChange={onAssetSellersChange}
          onAssetPhotoChange={onAssetPhotoChange}
          onAssetPhotoDelete={onAssetPhotoDelete}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onTagsChange={onTagsChange}
          onFieldDeleteClick={onAssetFieldDeleteClick}
          onAddNewFieldItem={onAssetFieldAddNewItem}
          onOpenNewSellerForm={onOpenNewSellerForm}
          onCloseNewSellerForm={onCloseNewSellerForm}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
        />
        <PortfolioModal
          isOpen={isPortfolioModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          portfolioId={FormService.FORM_KEY_NEW}
          formPrefixCommon="assetListData"
          portfolioGroupList={portfolioGroupList}
          portfolioMarkerColorList={portfolioMarkerColorList}
          saveFormDeps={saveFormDeps}
          onCloseModal={closePortfolioModal}
          onCreate={onPortfolioCreate}
          onCancel={onPortfolioModalCancel}
          onSubmitCreateForm={onSubmitCreateForm}
        />
        <PortfolioGroupModal
          isOpen={isPortfolioGroupModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          portfolioGroupPhotoFile={portfolioGroupPhotoFiles[FormService.FORM_KEY_NEW]}
          portfolioGroupPhotoChange={portfolioGroupPhotoChange}
          portfolioGroupPhotoDelete={portfolioGroupPhotoDelete}
          saveFormDeps={saveFormDeps}
          onCloseModal={closePortfolioGroupModal}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          withExportAll={withAllRowsSelected}
          portfolioIds={portfolioIdsFilter}
          portfolioGroupIds={portfolioGroupIdsFilter}
          entityIds={assetActiveIds}
          exportEntityType={IMPORT_EXPORT_ENTITY_TYPE_ASSET_LIST}
          exportFormatList={FORMAT_LIST_EXPORT_ASSET}
          onSuccess={onExportSuccess}
          onCloseModal={closeExportModal}
        />
        <ConfirmModal
          isOpen={isAssetDeleteConfirmModalOpen}
          title={assetDeleteConfirmModalTitle}
          text={assetDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeAssetDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isPortfolioDeleteConfirmModalOpen}
          title={portfolioDeleteConfirmModalTitle}
          text={portfolioDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onPortfolioDeleteConfirm}
          onCloseModal={closePortfolioDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    assetList,
    portfolioFilter,
    controls,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const assetListData: Portfolio[] = assetList.assetListData && assetList.assetListData.assetList;

  if (assetListData) {
    initialValues.assetListData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        capitalAllocated: null,
        markerColor: MARKER_COLOR_RED,
        assets: {
          [formKeyNew]: {
            id: FormService.FORM_KEY_NEW,
            name: null,
            status: ASSET_STATUS_UNREALIZED,
            maturityDate: null,
            seller: {},
          },
        },
      },
    };

    assetListData.forEach((portfolioItem: Portfolio): void => {
      const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioItem.id);

      initialValues.assetListData[formKeyPortfolio] = {
        id: portfolioItem.id,
        name: portfolioItem.name || null,
        capitalAllocated: FormatService.formatNumber(portfolioItem.capitalAllocated),
        groupId: portfolioItem.group ? portfolioItem.group.id : null,
        markerColor: portfolioItem.markerColor ? portfolioItem.markerColor.name : MARKER_COLOR_RED,
        assets: {},
      };

      portfolioItem.assets.forEach((assetItem: Asset): void => {
        const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetItem.id);

        initialValues.assetListData[formKeyPortfolio].assets[formKeyAsset] = {
          id: assetItem.id,
          name: assetItem.name || null,
          status: assetItem.status,
          size: FormatService.formatNumber(assetItem.size),
          sizeType: assetItem.sizeType ? assetItem.sizeType.name : null,
          loanAmount: FormatService.formatNumber(assetItem.loanAmount),
          amount: FormatService.formatNumber(assetItem.amount),
          value: FormatService.formatNumber(assetItem.value),
          addressDetails: assetItem.addressDetails,
          parcelNumber: assetItem.parcelNumber,
          submarket: assetItem.submarket,
          lotSize: assetItem.lotSize,
          assetOwnershipStructure: assetItem.assetOwnershipStructure,
          affordableUnits: assetItem.affordableUnits,
          asOfRightZoning: assetItem.asOfRightZoning,
          cost: FormatService.formatNumber(assetItem.cost),
          occupancy: assetItem.occupancy,
          currentNoi: FormatService.formatNumber(assetItem.currentNoi),
          ttmNoi: FormatService.formatNumber(assetItem.ttmNoi),
          runRateNoi: FormatService.formatNumber(assetItem.runRateNoi),
          forwardNoi: FormatService.formatNumber(assetItem.forwardNoi),
          maturityDate: assetItem.maturityDate ? DateService.parseDate(assetItem.maturityDate) : '',
          portfolioId: portfolioItem.id,
          address: assetItem.address,
          streetAddress: assetItem.streetAddress,
          location: assetItem.location,
          postalCode: assetItem.postalCode,
          sellerIds: assetItem.sellers && assetItem.sellers.length
            ? assetItem.sellers.map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
          portfolioInvestedEquity: FormatService.formatNumber(assetItem.portfolioInvestedEquity),
          leveredIrr: FormatService.formatNumber(assetItem.leveredIrr, true),
          equityMultiple: FormatService.formatNumber(assetItem.equityMultiple, true),
          loanNumber: assetItem.loanNumber,
          leedDesignation: assetItem.leedDesignation,
          ownerEntityName: assetItem.ownerEntityName,
          futureNoi: FormatService.formatNumber(assetItem.futureNoi, true),
          investmentEntityName: assetItem.investmentEntityName,
          investmentAmountCommitted: FormatService.formatNumber(assetItem.investmentAmountCommitted),
          investmentAmountUnfunded: FormatService.formatNumber(assetItem.investmentAmountUnfunded),
          investmentAmountFunded: FormatService.formatNumber(assetItem.investmentAmountFunded),
          currentMarketValue: FormatService.formatNumber(assetItem.currentMarketValue),
          closeDate: assetItem.closeDate ? DateService.parseDate(assetItem.closeDate) : '',
          extensions: assetItem.extensions,
          amountInvested: FormatService.formatNumber(assetItem.amountInvested),
          projectedStabilizedNoi: FormatService.formatNumber(assetItem.projectedStabilizedNoi),
          goingInCapRate: FormatService.formatNumber(assetItem.goingInCapRate, true),
          untrendedYieldToCost: FormatService.formatNumber(assetItem.untrendedYieldToCost),
          projectedUnleveredIrr: FormatService.formatNumber(assetItem.projectedUnleveredIrr, true),
          projectedUnleveredEquityMultiple: FormatService.formatNumber(assetItem.projectedUnleveredEquityMultiple),
          projectedUnleveredNetProfit: FormatService.formatNumber(assetItem.projectedUnleveredNetProfit),
          projectedLeveredIrr: FormatService.formatNumber(assetItem.projectedLeveredIrr, true),
          projectedLeveredEquityMultiple: FormatService.formatNumber(assetItem.projectedLeveredEquityMultiple),
          projectedLeveredNetProfit: FormatService.formatNumber(assetItem.projectedLeveredNetProfit),
          seniorLenderName: assetItem.seniorLenderName,
          seniorLoanAmount: FormatService.formatNumber(assetItem.seniorLoanAmount),
          seniorLoanClosingDate: assetItem.seniorLoanClosingDate
            ? DateService.parseDate(assetItem.seniorLoanClosingDate) : '',
          seniorLoanMaturityDate: assetItem.seniorLoanMaturityDate
            ? DateService.parseDate(assetItem.seniorLoanMaturityDate) : '',
          seniorLoanInterestRate: assetItem.seniorLoanInterestRate,
          seniorLoanInterestSpread: FormatService.formatNumber(assetItem.seniorLoanInterestSpread, true),
          seniorLoanLtv: FormatService.formatNumber(assetItem.seniorLoanLtv, true),
          seniorLoanLtc: FormatService.formatNumber(assetItem.seniorLoanLtc, true),
          mezzanineLenderName: assetItem.mezzanineLenderName,
          mezzanineLoanAmount: FormatService.formatNumber(assetItem.mezzanineLoanAmount),
          mezzanineLoanClosingDate: assetItem.mezzanineLoanClosingDate
            ? DateService.parseDate(assetItem.mezzanineLoanClosingDate) : '',
          mezzanineLoanMaturityDate: assetItem.mezzanineLoanMaturityDate
            ? DateService.parseDate(assetItem.mezzanineLoanMaturityDate) : '',
          mezzanineLoanInterestRate: assetItem.mezzanineLoanInterestRate,
          mezzanineLoanInterestSpread: FormatService.formatNumber(assetItem.mezzanineLoanInterestSpread, true),
          mezzanineLoanLtv: FormatService.formatNumber(assetItem.mezzanineLoanLtv, true),
          mezzanineLoanLtc: FormatService.formatNumber(assetItem.mezzanineLoanLtc, true),
          blendedLoanInterestRate: assetItem.blendedLoanInterestRate,
          blendedLoanInterestSpread: FormatService.formatNumber(assetItem.blendedLoanInterestSpread, true),
          notePrincipalBalanceAtOrigination: FormatService.formatNumber(assetItem.notePrincipalBalanceAtOrigination),
          accruedInterest: FormatService.formatNumber(assetItem.accruedInterest),
          marketType: assetItem.marketType,
          yearBuilt: FormatService.formatNumber(assetItem.yearBuilt, true),
          yearRenovated: FormatService.formatNumber(assetItem.yearRenovated, true),
          firstPaymentDate: assetItem.firstPaymentDate ? DateService.parseDate(assetItem.firstPaymentDate) : '',
          nextPaymentDueDate: assetItem.nextPaymentDueDate ? DateService.parseDate(assetItem.nextPaymentDueDate) : '',
          originalLoanTermMonth: FormatService.formatNumber(assetItem.originalLoanTermMonth, true),
          loanAge: FormatService.formatNumber(assetItem.loanAge, true),
          remainingTerm: FormatService.formatNumber(assetItem.remainingTerm, true),
          amortizationPeriodMonths: FormatService.formatNumber(assetItem.amortizationPeriodMonths, true),
          remainingAmortizationPeriod: FormatService.formatNumber(assetItem.remainingAmortizationPeriod, true),
          paymentStringYear: assetItem.paymentStringYear,
          amortizationType: assetItem.amortizationType,
          interestOnlyPeriod: FormatService.formatNumber(assetItem.interestOnlyPeriod, true),
          remainingInterestOnlyPeriod: FormatService.formatNumber(assetItem.remainingInterestOnlyPeriod, true),
          interestAccrualMethod: assetItem.interestAccrualMethod,
          primaryLoanPurpose: assetItem.primaryLoanPurpose,
          underlyingLoanType: assetItem.underlyingLoanType,
          lienPosition: FormatService.formatNumber(assetItem.lienPosition, true),
          primaryCollateral: assetItem.primaryCollateral,
          securityInterest: assetItem.securityInterest,
          groundLease: assetItem.groundLease,
          prepaymentProvisions: assetItem.prepaymentProvisions,
          remainingPrepaymentProvisions: assetItem.remainingPrepaymentProvisions,
          currentCreditLetterAmount: FormatService.formatNumber(assetItem.currentCreditLetterAmount),
          currentCreditLetterCreditExpirationDate: assetItem.currentCreditLetterCreditExpirationDate
            ? DateService.parseDate(assetItem.currentCreditLetterCreditExpirationDate) : '',
          interestReservesAtOrigination: FormatService.formatNumber(assetItem.interestReservesAtOrigination),
          taxInsuranceEscrowsAtOrigination: FormatService.formatNumber(assetItem.taxInsuranceEscrowsAtOrigination),
          capitalExpenditureReservesAtOrigination: FormatService.formatNumber(
            assetItem.capitalExpenditureReservesAtOrigination,
          ),
          otherEscrowAndReservesAtOrigination: FormatService.formatNumber(
            assetItem.otherEscrowAndReservesAtOrigination,
          ),
          currentInterestReserves: FormatService.formatNumber(assetItem.currentInterestReserves),
          currentTaxInsuranceEscrows: FormatService.formatNumber(assetItem.currentTaxInsuranceEscrows),
          currentCapitalExpenditureEscrows: FormatService.formatNumber(assetItem.currentCapitalExpenditureEscrows),
          currentOtherEscrowsAndReserves: FormatService.formatNumber(assetItem.currentOtherEscrowsAndReserves),
          initialAppraisalDate: assetItem.initialAppraisalDate
            ? DateService.parseDate(assetItem.initialAppraisalDate) : '',
          stabilizedAppraisedNoi: FormatService.formatNumber(assetItem.stabilizedAppraisedNoi),
          stabilizedOccupancy: FormatService.formatNumber(assetItem.stabilizedOccupancy, true),
          stabilizedCapitalizationRate: FormatService.formatNumber(assetItem.stabilizedCapitalizationRate, true),
          mostRecentAppraisalDate: assetItem.mostRecentAppraisalDate
            ? DateService.parseDate(assetItem.mostRecentAppraisalDate) : '',
          atCloseRevenue: FormatService.formatNumber(assetItem.atCloseRevenue),
          atCloseExpenses: FormatService.formatNumber(assetItem.atCloseExpenses),
          atCloseNoi: FormatService.formatNumber(assetItem.atCloseNoi),
          atCloseDebtYieldNoi: FormatService.formatNumber(assetItem.atCloseDebtYieldNoi, true),
          atCloseDscrNoi: assetItem.atCloseDscrNoi,
          atCloseNcf: assetItem.atCloseNcf,
          atCloseDebtYieldNcf: assetItem.atCloseDebtYieldNcf,
          atCloseDscrNcf: assetItem.atCloseDscrNcf,
          occupancyAtOrigination: FormatService.formatNumber(assetItem.occupancyAtOrigination, true),
          atCloseNoiAndOccupancyDate: assetItem.atCloseNoiAndOccupancyDate
            ? DateService.parseDate(assetItem.atCloseNoiAndOccupancyDate) : '',
          stabilizedNoiAtOrigination: assetItem.stabilizedNoiAtOrigination,
          stabilizedNcfAtOrigination: assetItem.stabilizedNcfAtOrigination,
          firstExtendedMaturityDate: assetItem.firstExtendedMaturityDate
            ? DateService.parseDate(assetItem.firstExtendedMaturityDate) : '',
          secondExtendedMaturityDate: assetItem.secondExtendedMaturityDate
            ? DateService.parseDate(assetItem.secondExtendedMaturityDate) : '',
          thirdExtendedMaturityDate: assetItem.thirdExtendedMaturityDate
            ? DateService.parseDate(assetItem.thirdExtendedMaturityDate) : '',
          yardiAssetId: assetItem.yardiAssetId,
          showInPortfolio: assetItem.showInPortfolio,
          types: assetItem.types,
          tags: assetItem.tags,
          options: {},
          mainPhoto: null,
        };

        if (assetItem.options && assetItem.options.length) {
          assetItem.options.forEach((assetFieldItem: AssetField): void => {
            const formAssetFieldItem: string = FormService.getIndexedFormFieldKey(assetFieldItem.id);

            initialValues.assetListData[formKeyPortfolio].assets[formKeyAsset].options[formAssetFieldItem] = {
              name: assetFieldItem.name,
              value: assetFieldItem.value,
            };
          });
        }
      });
    });
  }

  return {
    isDataLoaded: !!assetList.assetListData,
    isExportMode: controls.isExportMode,
    isSelectMode: controls.isSelectMode,
    isEditMode: controls.isEditMode,
    isCustomizeMode: controls.isCustomizeMode,
    isLoading: assetList.isLoading,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
    currentUser: app.baseData && app.baseData.currentUser,
    statisticsData: assetList.assetListData && assetList.assetListData.statisticsData,
    portfolioList: assetList.assetListData && assetList.assetListData.portfolioList,
    assetListData: assetList.assetListData && assetList.assetListData.assetList,
    assetsTotal: assetList.assetListData && assetList.assetListData.assetsTotal,
    assetListSummaryData: assetList.assetListData && assetList.assetListData.assetListSummaryData,
    assetTypeList: assetList.assetListData && assetList.assetListData.assetTypeList,
    assetStatusList: assetList.assetListData && assetList.assetListData.assetStatusList,
    measureUnitList: assetList.assetListData && assetList.assetListData.measureUnitList,
    pricingTypeList: assetList.assetListData && assetList.assetListData.pricingTypeList,
    assetOwnershipStructureTypeList: assetList.assetListData && assetList.assetListData.assetOwnershipStructureTypeList,
    portfolioMarkerColorList: assetList.assetListData && assetList.assetListData.portfolioMarkerColorList,
    portfolioGroupList: assetList.assetListData && assetList.assetListData.portfolioGroupList,
    contactCompanyList: assetList.assetListData && assetList.assetListData.contactCompanyList,
    tagList: assetList.assetListData && assetList.assetListData.tagList,
  };
};

const AssetListForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ assetList }: Reducers): ValidationErrorsMap => (
    assetList.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ assetList }: Reducers): ValidationErrorsMap => (
    assetList.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_ASSET_LIST}
      component={AssetList}
      validator={AssetListValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(AssetListForm);
